import {
  Scene,
  EntityBuilder,
  UIEntityBuilder,
  globalEventBus,
  globalTheme,
  globalTweens,
  InputSystem,
  TransformComponent,
  SpriteComponent,
  TextComponent,
  TRANSFORM_COMPONENT,
  SPRITE_COMPONENT,
  TEXT_COMPONENT,
  Easing,
  Time,
  KEYS,
  getScreenCategory,
} from 'agent-gamedev';
import type { IWorld, EntityId, SceneTransitionData } from 'agent-gamedev';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  GRID_COLS,
  GRID_ROWS,
  TILE_SIZE,
  CELL_EMPTY,
  CELL_WALL,
  CELL_BLOCK,
  CELL_STAR_BLOCK,
  CELL_HEART_BLOCK,
  CELL_BOMB,
  CELL_ENEMY_SPAWN,
  CELL_P1_SPAWN,
  CELL_P2_SPAWN,
  CELL_PLAYER,
  CELL_ITEM,
  CELL_SAFE,
  PLAYER_MOVE_COOLDOWN,
  PLAYER_MAX_HP,
  PLAYER_PUSH_DISTANCE,
  PLAYER_MAX_PUSH_DISTANCE,
  ENEMY_TYPE_FROG,
  ENEMY_TEXTURES,
  SCORE_HEART_MERGE,
  READY_DURATION,
  ENEMY_SPAWN_ACTIVATE_DELAY,
  TIME_LIMIT_SECONDS,
  getLevelTimeLimit,
  Z_WALL,
  Z_BLOCK,
  Z_ENEMY,
  Z_PLAYER,
  Z_UI_POPUP,
  Z_SCORE_POPUP,
  PALETTE,
  LEVELS,
  getCurrentLevelIndex,
  setCurrentLevelIndex,
  ASSETS,
  gridToWorld,
  gridKey,
  inBounds,
  isNpcSquirrelEnabled,
  NPC_HP,
  NPC_MOVE_COOLDOWN_MIN,
  NPC_SQUIRREL_INTRO_LEVEL_INDEX,
} from '../config';
import { getRunHp, getRunScore, setRunHp, setRunScore } from '../gameProgress';
import { gameAudio } from '../audio';
import type { PlayerState, EnemyState, NpcState, BombState, GamePhase, VictoryType, Player2State } from '../entity/types';
import { parseLevel, createFloor } from '../game/GridSystem';
import { resolveEnemySpawnCells, getEnemyMoveCooldown } from '../game/EnemySpawner';
import { checkHeartsConnected } from '../game/WinCondition';
import {
  killEnemyScore as _killEnemyScore, spawnScorePopup as _spawnScorePopup,
  damagePlayer as _damagePlayer, damageNpc as _damageNpc,
  type ComboState,
} from '../game/CombatSystem';
import {
  createHUD as _createHUD, createReadyOverlay as _createReadyOverlay,
  updateHUD as _updateHUD, setUIText,
  type HudEntities,
} from '../game/HudSystem';
import {
  isPushable, calculatePushPath,
  spawnItemAt, clearItemAt,
  pushBlock as _pushBlock, explodeSingleBomb as _explodeSingleBomb,
  pushBombUntilCollision as _pushBombUntilCollision,
  handleEdgePush as _handleEdgePush, collectItem as _collectItem,
  type BlockContext,
} from '../game/BlockSystem';
import { updateEnemies as _updateEnemies, type EnemyAIContext } from '../game/EnemyAI';
import { animateActorStepTransform } from '../game/AnimationUtils';
import { updateNpc as _updateNpc, tryPushNpc as _tryPushNpc, type NpcContext } from '../game/NpcController';
import { createGroundShadow, moveGroundShadowTo, type ShadowKind } from '../game/ShadowSystem';
import { multiplayerState } from '../network/MultiplayerState';
import type { NetMessage, PlayerAction } from '../network/types';
import { seedRandom, type RandomGenerator } from '../network/DeterministicRandom';
import { checkAABBCollision, getEntityBounds } from '../utils/aabb';

type E2EDirection = { dc: number; dr: number; name: string };

declare global {
  interface Window {
    __BRICKPUSH_E2E__?: {
      phase: GamePhase;
      isMultiplayer: boolean;
      levelIndex: number;
      moveCount: number;
      player: {
        col: number;
        row: number;
        moving: boolean;
        cooldown: number;
        hp: number;
      } | null;
      legalMoves: E2EDirection[];
    };
  }
}

const W = GAME_WIDTH;
const H = GAME_HEIGHT;

export class GameScene extends Scene {
  readonly name = 'GameScene';

  // ---- Grid state ----
  private grid: number[][] = [];
  private entityMap: Map<string, EntityId> = new Map();
  private itemDecorationMap: Map<string, EntityId[]> = new Map();
  private safeZones: Set<string> = new Set();
  private outerGrassZones: Set<string> = new Set();

  // ---- Entities ----
  private player: PlayerState | null = null;
  private player2: Player2State | null = null;
  private npc: NpcState | null = null;
  private enemies: EnemyState[] = [];
  private bombs: BombState[] = [];
  private movingBlocks: Set<EntityId> = new Set();
  private shadowMap: Map<EntityId, EntityId> = new Map();
  private playerMarkerMap: Map<EntityId, EntityId> = new Map();

  // ---- HUD entity references ----
  private hudEntities: HudEntities = {
    hpEntity: 0 as EntityId,
    scoreDisplayEntity: 0 as EntityId,
    timeDisplayEntity: 0 as EntityId,
    levelDisplayEntity: 0 as EntityId,
    heartStatusEntity: 0 as EntityId,
    enemyCountEntity: 0 as EntityId,
    scoreEntity: 0 as EntityId,
    collectEntity: 0 as EntityId,
    readyEntity: 0 as EntityId,
  };
  private readyEntity: EntityId = 0;
  private cutsceneActive = false;
  private cutsceneEntities: EntityId[] = [];

  // ---- Combo (delegated to CombatSystem) ----
  private combo: ComboState = { count: 0, timer: 0 };
  private get comboCount(): number { return this.combo.count; }
  private set comboCount(v: number) { this.combo.count = v; }
  private get comboTimer(): number { return this.combo.timer; }
  private set comboTimer(v: number) { this.combo.timer = v; }

  // ---- Non-grid entities (floor, background, effects, etc.) ----
  private nonGridEntities?: Set<EntityId>;

  // ---- Phase & Level ----
  private phase: GamePhase = 'ready';
  private readyTimer = READY_DURATION;
  private completeTimer = 0;
  private victoryType: VictoryType = 'none';
  private currentLevelIndex: number = 0;
  private timeLeft: number = TIME_LIMIT_SECONDS;
  private completionHandled = false;
  private deathSequenceStarted = false;

  // ---- Wave system ----
  private totalWaves = 1;
  private enemiesPerWave = 3;
  private currentWave = 0;
  private waveEnemyTypeIndex = 0;
  private nextWaveTimer = 0;
  private readonly WAVE_INTERVAL = 18;

  // ---- Touch joystick ----
  private touchDir: { dc: number; dr: number } | null = null;
  private touchActive = false;
  private touchHandlers: Array<{ evt: string; fn: () => void }> = [];
  private touchCleanup: (() => void) | null = null;
  private keyboardCleanup: (() => void) | null = null;

  // ---- Victory UI entities ----
  private victoryUIEntities: EntityId[] = [];

  // ---- Idle animation ----
  private idleTimer = 0;
  private idleFrame = 0;
  private npcIdleTimer = 0;
  private npcIdleFrame = 0;

  // ---- Multiplayer command sync ----
  protected isMultiplayer = false;
  private rng: RandomGenerator = Math.random;
  private multiplayerNextReady = new Set<0 | 1>();
  private multiplayerGameOverHandled = false;
  private multiplayerReadyStatusEntity: EntityId = 0 as EntityId;
  private multiplayerReadyCountdownEntity: EntityId = 0 as EntityId;
  private multiplayerReadyDeadlineMs = 0;
  private multiplayerDeathNoticeEntity: EntityId = 0 as EntityId;
  private suppressLocalInputUntilRelease = false;
  private pendingLocalDirections: Array<{ dc: number; dr: number }> = [];
  private localCommandSeq = 0;
  private e2eMoveCount = 0;
  private remoteCommandQueue: Array<{ playerId: 0 | 1; action: PlayerAction }> = [];
  private lastRemoteCommandSeq = new Map<0 | 1, number>();
  private multiplayerHp: Record<0 | 1, number> = { 0: PLAYER_MAX_HP, 1: PLAYER_MAX_HP };
  private deadMultiplayerPlayers = new Set<0 | 1>();
  private clearScheduled = false;

  // ---- Context builders per i moduli ----
  private get blockCtx(): BlockContext {
    return {
      grid: this.grid, entityMap: this.entityMap, itemDecorationMap: this.itemDecorationMap,
      safeZones: this.safeZones, outerGrassZones: this.outerGrassZones,
      bombs: this.bombs, enemies: this.enemies, player: this.player,
      isActive: this.isActive, phase: this.phase,
      trackEntity: (eid) => this.trackEntity(eid),
      findEnemyAt: (c, r) => this.findEnemyAt(c, r),
      destroyEnemy: (world, enemy) => this.destroyEnemy(world, enemy),
      damagePlayer: (world) => this.damagePlayer(world),
      combo: this.combo,
      destroyShadow: (world, eid) => this.destroyShadowFor(world, eid),
    };
  }

  private get enemyAICtx(): EnemyAIContext {
    const activePlayer = this.player && this.player.hp > 0
      ? this.player
      : (this.player2 && this.player2.hp > 0 ? this.player2 : this.player);
    const activePlayerId: 0 | 1 = activePlayer === this.player2 ? 1 : 0;
    return {
      grid: this.grid, entityMap: this.entityMap,
      player: activePlayer, npc: this.npc, enemies: this.enemies,
      findEnemyAt: (c, r) => this.findEnemyAt(c, r),
      damagePlayer: (world) => this.damagePlayer(world, activePlayerId),
      damageNpc: (world, npc) => this.damageNpc(world, npc),
      rng: this.rng,
      moveShadowTo: (world, eid, x, y, kind, duration) => this.moveShadowTo(world, eid, x, y, kind, duration),
      spawnTongueAttack: (world, fromC, fromR, toC, toR) => this.spawnTongueAttack(world, fromC, fromR, toC, toR),
    };
  }

  private get npcCtx(): NpcContext {
    return {
      grid: this.grid, enemies: this.enemies,
      findEnemyAt: (c, r) => this.findEnemyAt(c, r),
      pushBlock: (world, fC, fR, tC, tR, ct) => this.pushBlock(world, fC, fR, tC, tR, ct),
      crushEnemy: (world, p, enemy) => this.crushEnemy(world, p, enemy),
      movePlayerTo: (world, p, tc, tr) => this.movePlayerTo(world, p, tc, tr),
      moveShadowTo: (world, eid, x, y, kind, duration) => this.moveShadowTo(world, eid, x, y, kind, duration),
      rng: this.rng,
    };
  }

  onEnter(world: IWorld, _data?: SceneTransitionData): void {
    globalTheme.setTheme('retro');
    this.currentLevelIndex = getCurrentLevelIndex();
    this.isMultiplayer = multiplayerState.isMultiplayer;

    if (this.isMultiplayer) {
      const seeded = seedRandom(multiplayerState.gameSeed);
      this.rng = seeded.random;
      this.multiplayerHp = { 0: PLAYER_MAX_HP, 1: PLAYER_MAX_HP };
      this.deadMultiplayerPlayers.clear();
    } else {
      this.rng = Math.random;
    }

    this.resetState();
    this.timeLeft = getLevelTimeLimit(this.currentLevelIndex, Math.max(LEVELS.length, 1));
    this.registerNextLevelHandler(world);
    this.parseLevel();
    this.createFloor(world);
    this.createEntitiesFromGrid(world);
    this.createHUD(world);
    if (!this.maybeShowSquirrelCutscene(world)) {
      this.createReadyOverlay(world);
    }
    this.setupTouchJoystick();
    this.setupKeyboardInputQueue();

    if (this.isMultiplayer) {
      this.setupMultiplayerPeerHandlers(world);
    }
  }

  private registerNextLevelHandler(world: IWorld): void {
    this.unregisterHandlersFor(['custom:nextlevel', 'custom:multiplayer-next-ready']);
    const nextLevelHandler = () => {
      this.goToNextLevel(world);
    };
    globalEventBus.on('custom:nextlevel', nextLevelHandler);
    this.touchHandlers.push({ evt: 'custom:nextlevel', fn: nextLevelHandler });

    const multiplayerReadyHandler = () => {
      this.markMultiplayerNextReady(world);
    };
    globalEventBus.on('custom:multiplayer-next-ready', multiplayerReadyHandler);
    this.touchHandlers.push({ evt: 'custom:multiplayer-next-ready', fn: multiplayerReadyHandler });
  }

  private unregisterHandlersFor(events: string[]): void {
    const eventSet = new Set(events);
    const kept: Array<{ evt: string; fn: () => void }> = [];
    for (const handler of this.touchHandlers) {
      if (eventSet.has(handler.evt)) {
        globalEventBus.off(handler.evt, handler.fn);
      } else {
        kept.push(handler);
      }
    }
    this.touchHandlers = kept;
  }

  // ------------------------------------------------------------------
  // Reset
  // ------------------------------------------------------------------
  private resetState(): void {
    this.grid = [];
    this.entityMap.clear();
    this.itemDecorationMap.clear();
    this.safeZones.clear();
    this.outerGrassZones.clear();
    if (this.nonGridEntities) {
      this.nonGridEntities.clear();
    }
    this.player = null;
    this.player2 = null;
    this.npc = null;
    this.enemies = [];
    this.bombs = [];
    this.shadowMap.clear();
    this.playerMarkerMap.clear();
    this.phase = 'ready';
    this.readyTimer = READY_DURATION;
    this.completeTimer = 0;
    this.victoryType = 'none';
    this.deathSequenceStarted = false;
    this.timeLeft = TIME_LIMIT_SECONDS;
    this.touchDir = null;
    this.touchActive = false;
    this.victoryUIEntities = [];
    this.comboCount = 0;
    this.comboTimer = 0;
    this.combo = { count: 0, timer: 0 };
    this.totalWaves = 1;
    this.enemiesPerWave = 3;
    this.currentWave = 0;
    this.waveEnemyTypeIndex = 0;
    this.nextWaveTimer = 0;
    this.multiplayerNextReady.clear();
    this.multiplayerGameOverHandled = false;
    this.multiplayerReadyStatusEntity = 0 as EntityId;
    this.multiplayerReadyCountdownEntity = 0 as EntityId;
    this.multiplayerReadyDeadlineMs = 0;
    this.multiplayerDeathNoticeEntity = 0 as EntityId;
    this.suppressLocalInputUntilRelease = true;
    this.pendingLocalDirections = [];
    this.localCommandSeq = 0;
    this.e2eMoveCount = 0;
    this.remoteCommandQueue = [];
    this.lastRemoteCommandSeq.clear();
    this.clearScheduled = false;
  }

  // ------------------------------------------------------------------
  // Parse level into grid
  // ------------------------------------------------------------------
  private parseLevel(): void {
    parseLevel({ grid: this.grid, safeZones: this.safeZones, outerGrassZones: this.outerGrassZones, trackEntity: (eid) => this.trackEntity(eid) }, this.currentLevelIndex);
  }

  private createFloor(world: IWorld): void {
    createFloor(world, { grid: this.grid, safeZones: this.safeZones, outerGrassZones: this.outerGrassZones, trackEntity: (eid) => this.trackEntity(eid) });
  }

  // ------------------------------------------------------------------
  // Create entities from grid data
  // ------------------------------------------------------------------
  private createEntitiesFromGrid(world: IWorld): void {
    const levelData = LEVELS[this.currentLevelIndex];
    this.totalWaves = levelData?.enemyWaves ?? 1;
    this.enemiesPerWave = levelData?.enemiesPerWave ?? 3;
    this.currentWave = 0;
    this.waveEnemyTypeIndex = 0;

    for (let r = 0; r < GRID_ROWS; r++) {
      for (let c = 0; c < GRID_COLS; c++) {
        const raw = this.grid[r][c];
        const pos = gridToWorld(c, r);

        if (raw === CELL_WALL) {
          const eid = EntityBuilder.create(world, W, H)
            .withTransform({ x: pos.x, y: pos.y })
            .withSprite({ textureId: ASSETS.WALL, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_WALL })
            .build();
          this.trackEntity(eid);
          this.addShadowFor(world, eid, pos.x, pos.y, 'wall', Z_WALL - 1);
          this.entityMap.set(gridKey(c, r), eid);
        }

        if (raw === CELL_BLOCK || raw === CELL_STAR_BLOCK || raw === CELL_HEART_BLOCK) {
          const tex = raw === CELL_STAR_BLOCK ? ASSETS.STAR_BLOCK
            : raw === CELL_HEART_BLOCK ? ASSETS.HEART_BLOCK : ASSETS.BLOCK;
          const eid = EntityBuilder.create(world, W, H)
            .withTransform({ x: pos.x, y: pos.y })
            .withSprite({ textureId: tex, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_BLOCK })
            .build();
          this.trackEntity(eid);
          this.addShadowFor(world, eid, pos.x, pos.y, 'block', Z_BLOCK - 1);
          this.entityMap.set(gridKey(c, r), eid);
        }

        if (raw === CELL_BOMB) {
          const eid = EntityBuilder.create(world, W, H)
            .withTransform({ x: pos.x, y: pos.y })
            .withSprite({ textureId: ASSETS.BOMB_BLOCK, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_BLOCK })
            .build();
          this.trackEntity(eid);
          this.addShadowFor(world, eid, pos.x, pos.y, 'round', Z_BLOCK - 1);
          this.entityMap.set(gridKey(c, r), eid);
          this.bombs.push({ col: c, row: r, entity: eid, exploded: false });
        }

        if (raw === CELL_P1_SPAWN) {
          const eid = EntityBuilder.create(world, W, H)
            .withTransform({ x: pos.x, y: pos.y })
            .withSprite({ textureId: ASSETS.PLAYER1, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_PLAYER })
            .build();
          this.trackEntity(eid);
          this.addShadowFor(world, eid, pos.x, pos.y, 'actor', Z_PLAYER - 1);
          this.grid[r][c] = CELL_PLAYER;
          this.player = {
            col: c, row: r, entity: eid,
            moving: false, cooldown: 0, score: getRunScore(), collectibles: 0,
            hp: this.isMultiplayer ? this.multiplayerHp[0] : Math.min(getRunHp(), PLAYER_MAX_HP),
            damageCooldown: 0, isInvincible: false,
            pushDistance: PLAYER_PUSH_DISTANCE, canBreakWalls: false,
            inputLockTimer: 0,
          };

          if (this.shouldSpawnSquirrelNpc()) {
            const nc = c + 1;
            const nr = r;
            if (inBounds(nc, nr) && this.grid[nr][nc] === CELL_EMPTY) {
              const npcPos = gridToWorld(nc, nr);
              const neid = EntityBuilder.create(world, W, H)
                .withTransform({ x: npcPos.x, y: npcPos.y })
                .withSprite({ textureId: ASSETS.PLAYER2, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_PLAYER })
                .build();
              this.trackEntity(neid);
              this.addShadowFor(world, neid, npcPos.x, npcPos.y, 'actor', Z_PLAYER - 1);
              this.grid[nr][nc] = CELL_PLAYER;
              this.npc = {
                col: nc, row: nr, entity: neid,
                hp: NPC_HP, cooldown: NPC_MOVE_COOLDOWN_MIN,
                stunTimer: 0, moving: false,
                damageCooldown: 0, isInvincible: false,
              };
            }
          }
        }

        if (raw === CELL_P2_SPAWN && this.isMultiplayer) {
          const eid = EntityBuilder.create(world, W, H)
            .withTransform({ x: pos.x, y: pos.y })
            .withSprite({ textureId: ASSETS.PLAYER2, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_PLAYER })
            .build();
          this.trackEntity(eid);
          this.addShadowFor(world, eid, pos.x, pos.y, 'actor', Z_PLAYER - 1);
          this.grid[r][c] = CELL_PLAYER;
          this.player2 = {
            col: c, row: r, entity: eid,
            moving: false, cooldown: 0, score: 0, collectibles: 0,
            hp: this.multiplayerHp[1],
            damageCooldown: 0, isInvincible: false,
            pushDistance: PLAYER_PUSH_DISTANCE, canBreakWalls: false,
            inputLockTimer: 0,
          };
        }

        if (raw === CELL_ENEMY_SPAWN) {
          this.grid[r][c] = CELL_EMPTY;
        }
      }
    }

    this.ensureMultiplayerSecondPlayer(world);
    this.createMultiplayerPlayerMarkers(world);
  }

  private ensureMultiplayerSecondPlayer(world: IWorld): void {
    if (!this.isMultiplayer || this.player2 || !this.player) return;

    const fallbackOffsets = [
      { dc: 1, dr: 0 },
      { dc: -1, dr: 0 },
      { dc: 0, dr: 1 },
      { dc: 0, dr: -1 },
      { dc: 2, dr: 0 },
      { dc: -2, dr: 0 },
      { dc: 0, dr: 2 },
      { dc: 0, dr: -2 },
    ];

    for (const { dc, dr } of fallbackOffsets) {
      const c = this.player.col + dc;
      const r = this.player.row + dr;
      if (!inBounds(c, r)) continue;
      if (this.grid[r][c] !== CELL_EMPTY) continue;
      this.createPlayer2At(world, c, r);
      return;
    }
  }

  private createPlayer2At(world: IWorld, c: number, r: number): void {
    const pos = gridToWorld(c, r);
    const eid = EntityBuilder.create(world, W, H)
      .withTransform({ x: pos.x, y: pos.y })
      .withSprite({ textureId: ASSETS.PLAYER2, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_PLAYER })
      .build();
    this.trackEntity(eid);
    this.addShadowFor(world, eid, pos.x, pos.y, 'actor', Z_PLAYER - 1);
    this.grid[r][c] = CELL_PLAYER;
    this.player2 = {
      col: c, row: r, entity: eid,
      moving: false, cooldown: 0, score: 0, collectibles: 0,
      hp: this.multiplayerHp[1],
      damageCooldown: 0, isInvincible: false,
      pushDistance: PLAYER_PUSH_DISTANCE, canBreakWalls: false,
      inputLockTimer: 0,
    };
  }

  private createMultiplayerPlayerMarkers(world: IWorld): void {
    if (!this.isMultiplayer) return;
    if (this.player) this.createPlayerMarker(world, this.player.entity, 'P1', multiplayerState.localPlayerId === 0);
    if (this.player2) this.createPlayerMarker(world, this.player2.entity, 'P2', multiplayerState.localPlayerId === 1);
  }

  private createPlayerMarker(world: IWorld, playerEntity: EntityId, label: string, isLocal: boolean): void {
    if (this.playerMarkerMap.has(playerEntity)) return;
    const transform = world.getComponent<TransformComponent>(playerEntity, TRANSFORM_COMPONENT);
    if (!transform) return;
    const marker = EntityBuilder.create(world, W, H)
      .withTransform({ x: transform.x, y: transform.y - TILE_SIZE * 0.72 })
      .withText({
        text: isLocal ? `YOU ${label}` : label,
        fontSize: isLocal ? 15 : 13,
        color: isLocal ? 0x33ccff : 0xffd24a,
        align: 'center',
        zIndex: Z_PLAYER + 2,
      })
      .build();
    this.trackEntity(marker);
    this.playerMarkerMap.set(playerEntity, marker);
  }

  private movePlayerMarkerTo(world: IWorld, playerEntity: EntityId, x: number, y: number, duration = 0): void {
    const marker = this.playerMarkerMap.get(playerEntity);
    if (marker === undefined) return;
    const transform = world.getComponent<TransformComponent>(marker, TRANSFORM_COMPONENT);
    if (!transform) return;
    const target = { x, y: y - TILE_SIZE * 0.72 };
    globalTweens.killTweensOf(transform);
    if (duration > 0) globalTweens.to(transform, target, { duration, easing: Easing.easeOutQuad });
    else {
      transform.x = target.x;
      transform.y = target.y;
    }
  }


  /** 刷出下一波敌人 */
  private spawnNextWave(world: IWorld): void {
    if (this.currentWave >= this.totalWaves) return;
    this.currentWave++;

    const levelData = LEVELS[this.currentLevelIndex];
    const seq = levelData?.enemySequence ?? [ENEMY_TYPE_FROG];
    const count = this.enemiesPerWave;

    const types: number[] = [];
    for (let i = 0; i < count; i++) {
      types.push(seq[this.waveEnemyTypeIndex % seq.length]);
      this.waveEnemyTypeIndex++;
    }

    this.spawnEnemies(world, types, false);

    if (this.currentWave < this.totalWaves) {
      this.nextWaveTimer = this.WAVE_INTERVAL;
    }
  }

  private spawnEnemies(world: IWorld, enemyTypes: number[], activate = false): void {
    if (enemyTypes.length === 0) return;

    const spawnCells = this.resolveEnemySpawnCells(enemyTypes.length);
    for (let i = 0; i < enemyTypes.length; i++) {
      const type = enemyTypes[i];
      const spawn = spawnCells[i];
      const pos = gridToWorld(spawn.col, spawn.row);
      const textureId = activate
        ? (ENEMY_TEXTURES[type] ?? ASSETS.ENEMY_FROG)
        : ASSETS.ENEMY_INACTIVE;
      const eid = EntityBuilder.create(world, W, H)
        .withTransform({ x: pos.x, y: pos.y })
        .withSprite({ textureId, width: TILE_SIZE, height: TILE_SIZE, zIndex: Z_ENEMY })
        .build();
      this.trackEntity(eid);
      this.addShadowFor(world, eid, pos.x, pos.y, 'actor', Z_ENEMY - 1);
      this.grid[spawn.row][spawn.col] = CELL_ENEMY_SPAWN;
      this.enemies.push({
        col: spawn.col, row: spawn.row, entity: eid,
        type, active: activate, moveCooldown: getEnemyMoveCooldown(type, this.rng),
        stunTimer: 0,
        activateTimer: activate ? 0 : ENEMY_SPAWN_ACTIVATE_DELAY,
        dying: false,
      });
      // 未激活的敌人添加摇晃出生动画
      if (!activate) {
        const t = world.getComponent<TransformComponent>(eid, TRANSFORM_COMPONENT);
        if (t) {
          globalTweens.to(t, { rotation: 0.25 }, {
            duration: 0.12, easing: Easing.easeInOutQuad, yoyo: true, repeat: -1,
          });
        }
      }
    }
  }

  private resolveEnemySpawnCells(count: number): Array<{ col: number; row: number }> {
    return resolveEnemySpawnCells({
      grid: this.grid,
      playerCol: this.player?.col ?? 7,
      playerRow: this.player?.row ?? 6,
      levelIndex: this.currentLevelIndex,
    }, count);
  }

  // ------------------------------------------------------------------
  // HUD
  // ------------------------------------------------------------------
  private createHUD(world: IWorld): void {
    const initHp = this.player?.hp ?? getRunHp();
    this.hudEntities = _createHUD(world, this.currentLevelIndex, initHp, (eid) => this.trackEntity(eid));
  }

  private createReadyOverlay(world: IWorld): void {
    this.readyEntity = _createReadyOverlay(world, (eid) => this.trackEntity(eid));
  }

  private shouldSpawnSquirrelNpc(): boolean {
    return !this.isMultiplayer && (this.currentLevelIndex >= NPC_SQUIRREL_INTRO_LEVEL_INDEX || isNpcSquirrelEnabled());
  }

  private maybeShowSquirrelCutscene(world: IWorld): boolean {
    if (this.isMultiplayer || this.currentLevelIndex !== NPC_SQUIRREL_INTRO_LEVEL_INDEX) return false;
    this.cutsceneActive = true;

    const panel = EntityBuilder.create(world, W, H)
      .withTransform({ x: W / 2, y: H / 2, screenSpace: true })
      .withSprite({ color: 0x111827, width: 620, height: 500, zIndex: Z_UI_POPUP + 5 })
      .build();
    this.trackEntity(panel);
    this.cutsceneEntities.push(panel);

    const image = EntityBuilder.create(world, W, H)
      .withTransform({ x: W / 2, y: H / 2 - 58, screenSpace: true })
      .withSprite({ textureId: ASSETS.CUTSCENE_SQUIRREL, width: 560, height: 333, zIndex: Z_UI_POPUP + 6 })
      .build();
    this.trackEntity(image);
    this.cutsceneEntities.push(image);

    const title = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 138, width: 560, height: 42 })
      .withText({ text: 'ROUND-04  新队友入职', fontSize: 30, color: PALETTE.TITLE_YELLOW, align: 'center', zIndex: Z_UI_POPUP + 7 })
      .build();
    this.trackEntity(title);
    this.cutsceneEntities.push(title);

    const desc = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 180, width: 560, height: 36 })
      .withText({ text: '它会帮你推箱子，也可能把计划推翻。', fontSize: 20, color: 0xffd6a5, align: 'center', zIndex: Z_UI_POPUP + 7 })
      .build();
    this.trackEntity(desc);
    this.cutsceneEntities.push(desc);

    const button = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 232, width: 220, height: 48 })
      .withButton({ label: '开始合作', onClick: 'custom:dismisscutscene', borderRadius: 8 })
      .build();
    this.trackEntity(button);
    this.cutsceneEntities.push(button);

    const dismiss = () => this.dismissCutscene(world);
    globalEventBus.on('custom:dismisscutscene', dismiss);
    this.touchHandlers.push({ evt: 'custom:dismisscutscene', fn: dismiss });
    return true;
  }

  private dismissCutscene(world: IWorld): void {
    if (!this.cutsceneActive) return;
    this.cutsceneActive = false;
    this.unregisterHandlersFor(['custom:dismisscutscene']);
    for (const eid of this.cutsceneEntities) {
      world.destroyEntity(eid);
    }
    this.cutsceneEntities = [];
    this.createReadyOverlay(world);
  }

  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Touch joystick — 屏幕任意位置按下即可拖动控制方向
  // ------------------------------------------------------------------
  private setupTouchJoystick(): void {
    const screen = getScreenCategory(W, H);
    if (screen.category === 'desktop' || screen.category === 'large') return;

    const container = document.getElementById('game-container');
    if (!container) return;

    // 创建摇杆视觉元素
    const base = document.createElement('div');
    base.dataset.testid = 'touch-joystick-base';
    base.style.cssText = 'position:fixed;width:100px;height:100px;border-radius:50%;background:rgba(255,255,255,0.12);border:2px solid rgba(255,255,255,0.25);pointer-events:none;display:none;z-index:100;';
    const stick = document.createElement('div');
    stick.dataset.testid = 'touch-joystick-stick';
    stick.style.cssText = 'position:absolute;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.35);top:30px;left:30px;pointer-events:none;transition:none;';
    base.appendChild(stick);
    document.body.appendChild(base);

    let touchId: number | null = null;
    let startX = 0;
    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (touchId !== null) return;
      const touch = e.changedTouches[0];
      touchId = touch.identifier;
      startX = touch.clientX;
      startY = touch.clientY;
      this.touchActive = true;

      base.style.display = 'block';
      base.style.left = (startX - 50) + 'px';
      base.style.top = (startY - 50) + 'px';
      stick.style.transform = 'translate(0px, 0px)';
      e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!this.touchActive || touchId === null) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== touchId) continue;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 30;
        const clampedDist = Math.min(dist, maxDist);
        const angle = Math.atan2(dy, dx);

        stick.style.transform = `translate(${Math.cos(angle) * clampedDist}px, ${Math.sin(angle) * clampedDist}px)`;

        if (dist > 12) {
          if (Math.abs(dx) > Math.abs(dy)) {
            this.touchDir = { dc: dx > 0 ? 1 : -1, dr: 0 };
          } else {
            this.touchDir = { dc: 0, dr: dy > 0 ? 1 : -1 };
          }
        } else {
          this.touchDir = null;
        }
        e.preventDefault();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === touchId) {
          touchId = null;
          this.touchActive = false;
          this.touchDir = null;
          base.style.display = 'none';
        }
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);

    this.touchCleanup = () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      base.remove();
    };
  }

  private setupKeyboardInputQueue(): void {
    if (!this.isMultiplayer || typeof window === 'undefined') return;
    if (this.keyboardCleanup) return;

    const toDirection = (key: string): { dc: number; dr: number } | null => {
      const normalized = key.toLowerCase();
      if (normalized === 'w' || key === 'ArrowUp') return { dc: 0, dr: -1 };
      if (normalized === 's' || key === 'ArrowDown') return { dc: 0, dr: 1 };
      if (normalized === 'a' || key === 'ArrowLeft') return { dc: -1, dr: 0 };
      if (normalized === 'd' || key === 'ArrowRight') return { dc: 1, dr: 0 };
      return null;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!this.isActive || !this.isMultiplayer || this.phase !== 'playing') return;
      if (event.repeat) return;
      const dir = toDirection(event.key);
      if (!dir) return;
      event.preventDefault();
      if (this.suppressLocalInputUntilRelease) return;
      if (this.pendingLocalDirections.length === 0) {
        this.pendingLocalDirections.push(dir);
      } else {
        this.pendingLocalDirections[0] = dir;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    this.keyboardCleanup = () => {
      window.removeEventListener('keydown', onKeyDown);
      this.keyboardCleanup = null;
    };
  }

  // ------------------------------------------------------------------
  // update
  // ------------------------------------------------------------------
  update(world: IWorld, _deltaTime: number): void {
    const dt = Time.deltaTime;

    if (this.cutsceneActive) {
      const input = world.getSystem<InputSystem>('input');
      if (input?.isKeyDown(KEYS.SPACE) || input?.isKeyDown(KEYS.ENTER)) {
        this.dismissCutscene(world);
      }
      return;
    }

    if (this.phase === 'ready') {
      if (this.isMultiplayer) {
        this.updateLocalInputSuppression(world);
      }
      this.readyTimer -= dt;
      if (this.readyTimer <= 0) {
        this.phase = 'playing';
        this.activateEnemies(world);
        this.hideReady(world);
      }
      return;
    }

    if (this.phase === 'playing') {
      if (this.isMultiplayer) {
        this.updateMultiplayer(world, dt);
      } else {
        this.updateSinglePlayer(world, dt);
      }
      return;
    }

    if (this.phase === 'complete') {
      if (this.isMultiplayer) {
        this.updateMultiplayerReadyCountdown(world);
        this.updateHUD(world);
        return;
      }

      this.completeTimer -= dt;
      if (this.completeTimer <= 0 && !this.completionHandled) {
        this.completionHandled = true;
        const hasNextLevel = LEVELS.length > 0 && this.currentLevelIndex < LEVELS.length - 1;
        if (this.victoryType === 'none' || !hasNextLevel) {
          const score = this.getResolvedScore();
          setRunScore(score);
          globalEventBus.emit('scene:gameover', {
            score,
            victoryType: this.victoryType,
            levelName: LEVELS[this.currentLevelIndex]?.name ?? `ROUND-${this.currentLevelIndex + 1}`,
            canSubmitScore: score > 0,
          });
        }
      }
    }
  }

  private updateSinglePlayer(world: IWorld, dt: number): void {
    this.syncRunProgress();
    if (this.timeLeft > 0) {
      this.timeLeft -= dt;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.gameOver(world, 'time');
      }
    }

    this.updateEnemies(world, dt);
    this.updateWaves(world, dt);
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.comboCount = 0;
    }
    this.handlePlayerInput(world, dt);
    this.updateNpc(world, dt);
    this.updateHUD(world);
    this.checkComplete(world);
    this.updateIdleAnimations(world, dt);
    this.checkAABBCollisions(world);
    this.publishE2EState();
  }

  private updateMultiplayer(world: IWorld, dt: number): void {
    this.syncRunProgress();
    this.updateMultiplayerPlayerTimers(dt);
    if (this.timeLeft > 0) {
      this.timeLeft -= dt;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.gameOver(world, 'time');
        return;
      }
    }

    this.processLocalPlayerCommand(world);
    this.processRemotePlayerCommands(world);
    this.updateEnemies(world, dt);
    this.updateWaves(world, dt);
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.comboCount = 0;
    }
    this.updateNpc(world, dt);
    this.updateHUD(world);
    this.checkComplete(world);
    this.updateIdleAnimations(world, dt);
    this.checkAABBCollisions(world);

    // 超时检测
    if (multiplayerState.peer && !multiplayerState.peer.isOpen && multiplayerState.connected) {
      this.finishMultiplayerGameOver(world, 'disconnect', false);
      multiplayerState.cleanup();
    }
  }

  private setupMultiplayerPeerHandlers(world: IWorld): void {
    const peer = multiplayerState.peer;
    if (!peer) return;

    peer.onMessage((raw) => {
      let msg: NetMessage;
      try {
        msg = JSON.parse(raw) as NetMessage;
      } catch {
        return;
      }

      switch (msg.type) {
        case 'handshake':
          multiplayerState.gameSeed = multiplayerState.gameSeed ^ msg.seed;
          break;
        case 'input':
          // Old lockstep packets are ignored by the command-sync multiplayer path.
          break;
        case 'player-command':
          this.enqueueRemotePlayerCommand(msg);
          break;
        case 'next-level-ready':
          if (msg.levelIndex === this.currentLevelIndex) {
            this.multiplayerNextReady.add(msg.playerId);
            this.refreshMultiplayerReadyStatus(world);
            this.maybeStartMultiplayerNextLevel(world);
          }
          break;
        case 'load-level':
          this.loadMultiplayerLevel(world, msg.levelIndex, msg.seed);
          break;
        case 'multiplayer-game-over':
          this.finishMultiplayerGameOver(world, msg.reason, false);
          break;
        case 'player-health':
          this.applyRemotePlayerHealth(world, msg);
          break;
        case 'player-power':
          this.applyRemotePlayerPower(msg);
          break;
        case 'level-clear':
          this.applyRemoteLevelClear(world, msg);
          break;
        case 'ping':
          break;
      }
    });
  }

  private sendMultiplayerMessage(msg: NetMessage): void {
    if (multiplayerState.peer?.isOpen) {
      multiplayerState.peer.send(JSON.stringify(msg));
    }
  }

  private getInputSystem(world: IWorld): InputSystem | null {
    return world.getSystem<InputSystem>('InputSystem') ?? world.getSystem<InputSystem>('input') ?? null;
  }

  private readLocalDirection(world: IWorld, includeKeyboard = true): { dc: number; dr: number } | null {
    const input = this.getInputSystem(world);
    let dc = 0;
    let dr = 0;

    if (includeKeyboard && input) {
      if (input.isKeyDown(KEYS.W) || input.isKeyDown(KEYS.UP)) dr = -1;
      else if (input.isKeyDown(KEYS.S) || input.isKeyDown(KEYS.DOWN)) dr = 1;
      else if (input.isKeyDown(KEYS.A) || input.isKeyDown(KEYS.LEFT)) dc = -1;
      else if (input.isKeyDown(KEYS.D) || input.isKeyDown(KEYS.RIGHT)) dc = 1;
    }

    if (dc === 0 && dr === 0 && this.touchDir && this.touchActive) {
      dc = this.touchDir.dc;
      dr = this.touchDir.dr;
    }

    return dc !== 0 || dr !== 0 ? { dc, dr } : null;
  }

  private updateLocalInputSuppression(world: IWorld): void {
    if (this.readLocalDirection(world) === null) {
      this.suppressLocalInputUntilRelease = false;
    }
  }

  private collectLocalInput(world: IWorld): PlayerAction[] {
    const p = multiplayerState.localPlayerId === 0 ? this.player : this.player2;
    if (!p) return [{ type: 'none' }];

    if (p.damageCooldown > 0) return [{ type: 'none' }];
    if (p.inputLockTimer > 0) return [{ type: 'none' }];
    if (p.cooldown > 0) return [{ type: 'none' }];
    if (p.moving) return [{ type: 'none' }];

    const queuedDir = this.pendingLocalDirections.shift();
    if (queuedDir) {
      this.suppressLocalInputUntilRelease = false;
      return [{ type: 'move', dc: queuedDir.dc, dr: queuedDir.dr }];
    }

    const dir = this.readLocalDirection(world, true);
    if (!dir) {
      this.suppressLocalInputUntilRelease = false;
      return [{ type: 'none' }];
    }

    if (this.suppressLocalInputUntilRelease) {
      return [{ type: 'none' }];
    }

    return [{ type: 'move', dc: dir.dc, dr: dir.dr }];
  }

  private updateMultiplayerPlayerTimers(dt: number): void {
    this.tickPlayerTimers(this.player, dt);
    this.tickPlayerTimers(this.player2, dt);
  }

  private tickPlayerTimers(player: PlayerState | Player2State | null, dt: number): void {
    if (!player || player.hp <= 0) return;
    if (player.damageCooldown > 0) {
      player.damageCooldown -= dt;
      if (player.damageCooldown <= 0) player.isInvincible = false;
    }
    if (player.inputLockTimer > 0) {
      player.inputLockTimer -= dt;
    }
    if (player.cooldown > 0) {
      player.cooldown -= dt;
    }
  }

  private processLocalPlayerCommand(world: IWorld): void {
    const action = this.collectLocalInput(world)[0];
    if (!action || action.type === 'none') return;
    const playerId = multiplayerState.localPlayerId;
    if (!this.tryApplyPlayerCommand(world, playerId, action)) return;
    this.sendMultiplayerMessage({
      type: 'player-command',
      playerId,
      levelIndex: this.currentLevelIndex,
      seq: ++this.localCommandSeq,
      action,
    });
  }

  private enqueueRemotePlayerCommand(msg: Extract<NetMessage, { type: 'player-command' }>): void {
    if (!this.isMultiplayer || msg.levelIndex !== this.currentLevelIndex) return;
    if (msg.playerId === multiplayerState.localPlayerId) return;
    const lastSeq = this.lastRemoteCommandSeq.get(msg.playerId) ?? 0;
    if (msg.seq <= lastSeq) return;
    this.lastRemoteCommandSeq.set(msg.playerId, msg.seq);
    this.remoteCommandQueue.push({ playerId: msg.playerId, action: msg.action });
  }

  private processRemotePlayerCommands(world: IWorld): void {
    if (this.remoteCommandQueue.length === 0) return;
    const next = this.remoteCommandQueue[0];
    if (this.tryApplyPlayerCommand(world, next.playerId, next.action)) {
      this.remoteCommandQueue.shift();
    }
  }

  private tryApplyPlayerCommand(world: IWorld, playerId: 0 | 1, action: PlayerAction): boolean {
    if (action.type === 'none') return true;
    const player = playerId === 0 ? this.player : this.player2;
    if (!player) return false;
    if (player.hp <= 0) return true;
    if (player.moving || player.cooldown > 0 || player.inputLockTimer > 0 || player.damageCooldown > 0) {
      return false;
    }
    if (playerId === 0 && this.player) {
      const before = { col: this.player.col, row: this.player.row };
      this.tryMovePlayer(world, this.player, action.dc, action.dr);
      if (this.player.col === before.col && this.player.row === before.row && this.player.cooldown <= 0) {
        this.player.cooldown = PLAYER_MOVE_COOLDOWN;
      }
      return true;
    }
    if (playerId === 1 && this.player2) {
      const before = { col: this.player2.col, row: this.player2.row };
      this.tryMovePlayer2(world, this.player2, action.dc, action.dr);
      if (this.player2.col === before.col && this.player2.row === before.row && this.player2.cooldown <= 0) {
        this.player2.cooldown = PLAYER_MOVE_COOLDOWN;
      }
      return true;
    }
    return false;
  }

  // ------------------------------------------------------------------
  // Activate enemies
  // ------------------------------------------------------------------
  private activateEnemies(world: IWorld): void {
    this.spawnNextWave(world);
  }

  private hideReady(world: IWorld): void {
    if (this.readyEntity) {
      world.destroyEntity(this.readyEntity);
    }
  }

  // ------------------------------------------------------------------
  // Player input (single player)
  // ------------------------------------------------------------------
  private handlePlayerInput(world: IWorld, dt: number): void {
    if (this.isMultiplayer) return;
    const p = this.player;
    if (!p) return;

    if (p.damageCooldown > 0) {
      p.damageCooldown -= dt;
      if (p.damageCooldown <= 0) {
        p.isInvincible = false;
      }
    }

    if (p.inputLockTimer > 0) {
      p.inputLockTimer -= dt;
      return;
    }

    if (p.cooldown > 0) { p.cooldown -= dt; return; }
    if (p.moving) return;

    const input = world.getSystem<InputSystem>('InputSystem');
    if (!input) return;

    let dc = 0;
    let dr = 0;

    if (input.isKeyDown(KEYS.W) || input.isKeyDown(KEYS.UP)) dr = -1;
    else if (input.isKeyDown(KEYS.S) || input.isKeyDown(KEYS.DOWN)) dr = 1;
    else if (input.isKeyDown(KEYS.A) || input.isKeyDown(KEYS.LEFT)) dc = -1;
    else if (input.isKeyDown(KEYS.D) || input.isKeyDown(KEYS.RIGHT)) dc = 1;

    if (dc === 0 && dr === 0 && this.touchDir && this.touchActive) {
      dc = this.touchDir.dc;
      dr = this.touchDir.dr;
    }

    if (dc !== 0 || dr !== 0) {
      this.tryMovePlayer(world, p, dc, dr);
    }
  }

  // ------------------------------------------------------------------
  // Try to move player (P1)
  // ------------------------------------------------------------------
  private tryMovePlayer(world: IWorld, p: PlayerState, dc: number, dr: number): void {
    const tc = p.col + dc;
    const tr = p.row + dr;
    if (!inBounds(tc, tr)) return;

    const targetCell = this.grid[tr][tc];

    const enemy = this.findEnemyAt(tc, tr);
    if (enemy) {
      if (!enemy.active) {
        // inactive enemy: pass through
      } else {
        this.damagePlayer(world, 0);
        this.startEnemyRetreat(enemy, p.col, p.row);
        p.cooldown = PLAYER_MOVE_COOLDOWN;
        return;
      }
    }

    if (this.npc && this.npc.col === tc && this.npc.row === tr) {
      if (p.canBreakWalls && !this.npc.isInvincible) {
        this.tryPushNpc(world, p, tc, tr, dc, dr);
      } else {
        p.cooldown = PLAYER_MOVE_COOLDOWN;
      }
      return;
    }

    if (targetCell === CELL_EMPTY || targetCell === CELL_SAFE) {
      this.movePlayerTo(world, p, tc, tr);
      return;
    }

    if (targetCell === CELL_ITEM) {
      this.collectItem(world, p, tc, tr);
      this.movePlayerTo(world, p, tc, tr);
      return;
    }

    if (targetCell === CELL_BOMB) {
      this.pushBombUntilCollision(world, p, tc, tr, dc, dr);
      return;
    }

    if (this.isPushable(targetCell, p.canBreakWalls)) {
      const pushDistance = this.getPushDistanceForCell(targetCell, p);
      const { finalC, finalR, distance } = this.calculatePushPath(tc, tr, dc, dr, pushDistance, p.canBreakWalls);

      if (distance > 0) {
        for (let i = 1; i <= distance; i++) {
          const checkC = tc + dc * i;
          const checkR = tr + dr * i;
          const enemyInPath = this.findEnemyAt(checkC, checkR);
          if (enemyInPath) {
            this.crushEnemy(world, p, enemyInPath);
          }
        }
        this.pushBlock(world, tc, tr, finalC, finalR, targetCell);
        this.movePlayerTo(world, p, tc, tr);
        return;
      } else {
        this.handleEdgePush(world, p, tc, tr, targetCell, dc, dr);
        return;
      }
    }
  }

  // ------------------------------------------------------------------
  // Try to move player 2 (multiplayer)
  // ------------------------------------------------------------------
  private tryMovePlayer2(world: IWorld, p2: Player2State, dc: number, dr: number): void {
    const tc = p2.col + dc;
    const tr = p2.row + dr;
    if (!inBounds(tc, tr)) return;

    const targetCell = this.grid[tr][tc];

    const enemy = this.findEnemyAt(tc, tr);
    if (enemy) {
      if (!enemy.active) {
        // pass through
      } else {
        this.damagePlayer(world, 1);
        this.startEnemyRetreat(enemy, p2.col, p2.row);
        p2.cooldown = PLAYER_MOVE_COOLDOWN;
        return;
      }
    }

    if (this.player && this.player.col === tc && this.player.row === tr) {
      p2.cooldown = PLAYER_MOVE_COOLDOWN;
      return;
    }

    if (targetCell === CELL_EMPTY || targetCell === CELL_SAFE) {
      this.movePlayer2To(world, p2, tc, tr);
      return;
    }

    if (targetCell === CELL_ITEM) {
      this.collectItemAsPlayer2(world, p2, tc, tr);
      this.movePlayer2To(world, p2, tc, tr);
      return;
    }

    if (targetCell === CELL_BOMB) {
      this.pushBombUntilCollisionPlayer2(world, p2, tc, tr, dc, dr);
      return;
    }

    if (this.isPushable(targetCell, p2.canBreakWalls)) {
      const pushDistance = this.getPushDistanceForCell(targetCell, p2);
      const { finalC, finalR, distance } = this.calculatePushPath(tc, tr, dc, dr, pushDistance, p2.canBreakWalls);

      if (distance > 0) {
        for (let i = 1; i <= distance; i++) {
          const checkC = tc + dc * i;
          const checkR = tr + dr * i;
          const enemyInPath = this.findEnemyAt(checkC, checkR);
          if (enemyInPath && this.player) {
            this.crushEnemy(world, this.player, enemyInPath);
          }
        }
        this.pushBlock(world, tc, tr, finalC, finalR, targetCell);
        this.movePlayer2To(world, p2, tc, tr);
        return;
      } else {
        this.handleEdgePushPlayer2(world, p2, tc, tr, targetCell, dc, dr);
        return;
      }
    }
  }

  private movePlayer2To(world: IWorld, p2: Player2State, tc: number, tr: number): void {
    const dc = tc - p2.col;
    const dr = tr - p2.row;
    this.grid[p2.row][p2.col] = this.safeZones.has(gridKey(p2.col, p2.row)) ? CELL_SAFE : CELL_EMPTY;
    this.grid[tr][tc] = CELL_PLAYER;

    p2.col = tc;
    p2.row = tr;
    p2.cooldown = PLAYER_MOVE_COOLDOWN;
    p2.moving = true;
    gameAudio.playWalk();

    const target = gridToWorld(tc, tr);
    this.moveShadowTo(world, p2.entity, target.x, target.y, 'actor', PLAYER_MOVE_COOLDOWN);
    this.movePlayerMarkerTo(world, p2.entity, target.x, target.y, PLAYER_MOVE_COOLDOWN);
    const transform = world.getComponent<TransformComponent>(p2.entity, TRANSFORM_COMPONENT);
    if (transform) {
      animateActorStepTransform(transform, target.x, target.y, dc, dr, PLAYER_MOVE_COOLDOWN);
      setTimeout(() => { p2.moving = false; }, Math.round(PLAYER_MOVE_COOLDOWN * 1000));
    } else {
      p2.moving = false;
    }
  }


  // ---- BlockSystem delegates ----
  private isPushable(cell: number, canPushWalls = false): boolean { return isPushable(cell, canPushWalls); }
  private getPushDistanceForCell(cell: number, p: Pick<PlayerState, 'pushDistance' | 'canBreakWalls'>): number {
    if (cell !== CELL_WALL) return p.pushDistance;
    return p.canBreakWalls ? Math.max(1, p.pushDistance - 1) : 0;
  }
  private calculatePushPath(sC: number, sR: number, dc: number, dr: number, max: number, canPushWalls = false) { return calculatePushPath(this.blockCtx, sC, sR, dc, dr, max, canPushWalls); }
  private spawnItemAt(world: IWorld, c: number, r: number, tex: string) { spawnItemAt(world, this.blockCtx, c, r, tex); }
  private pushBlock(world: IWorld, fC: number, fR: number, tC: number, tR: number, ct: number): number {
    const blockEntity = this.entityMap.get(gridKey(fC, fR));
    const duration = _pushBlock(world, this.blockCtx, fC, fR, tC, tR, ct);
    if (blockEntity !== undefined && duration > 0) {
      this.movingBlocks.add(blockEntity);
      setTimeout(() => this.movingBlocks.delete(blockEntity), Math.round(duration * 1000));
      const target = gridToWorld(tC, tR);
      this.moveShadowTo(world, blockEntity, target.x, target.y, 'block', duration);
    }
    return duration;
  }
  private pushBombUntilCollision(world: IWorld, p: PlayerState, bC: number, bR: number, dc: number, dr: number) {
    _pushBombUntilCollision(world, this.blockCtx, p, bC, bR, dc, dr, (w, pl, tc, tr) => this.movePlayerTo(w, pl, tc, tr));
  }
  private pushBombUntilCollisionPlayer2(world: IWorld, p2: Player2State, bC: number, bR: number, dc: number, dr: number) {
    _pushBombUntilCollision(world, this.blockCtx, this.player!, bC, bR, dc, dr, (w, _pl, tc, tr) => this.movePlayer2To(w, p2, tc, tr));
  }
  private handleEdgePush(world: IWorld, p: PlayerState, bC: number, bR: number, ct: number, _dc: number, _dr: number) {
    _handleEdgePush(world, this.blockCtx, p, bC, bR, ct, (w, pl, tc, tr) => this.movePlayerTo(w, pl, tc, tr), (w, c, r, v, col) => this.spawnScorePopup(w, c, r, v, col));
  }
  private handleEdgePushPlayer2(world: IWorld, p2: Player2State, bC: number, bR: number, ct: number, _dc: number, _dr: number) {
    _handleEdgePush(world, this.blockCtx, this.player!, bC, bR, ct, (w, _pl, tc, tr) => this.movePlayer2To(w, p2, tc, tr), (w, c, r, v, col) => this.spawnScorePopup(w, c, r, v, col));
  }
  private collectItem(world: IWorld, p: PlayerState, c: number, r: number) {
    _collectItem(world, this.blockCtx, p, c, r, (w, c2, r2, v, col) => this.spawnScorePopup(w, c2, r2, v, col), (w, c2, r2, t, col) => this.spawnPowerupText(w, c2, r2, t, col));
    if (this.isMultiplayer && multiplayerState.localPlayerId === 0) this.sendMultiplayerPower(0, p);
  }
  private collectItemAsPlayer2(world: IWorld, p2: Player2State, c: number, r: number) {
    if (!this.player) return;
    const key = gridKey(c, r);
    const itemEntity = this.entityMap.get(key);
    if (itemEntity !== undefined) {
      const sprite = world.getComponent<SpriteComponent>(itemEntity, SPRITE_COMPONENT);
      if (sprite && sprite.textureId === ASSETS.PUSH_POWERUP) {
        p2.pushDistance = Math.min(p2.pushDistance + 1, PLAYER_MAX_PUSH_DISTANCE);
        p2.canBreakWalls = true;
        this.spawnPowerupText(world, c, r, `箱子${p2.pushDistance} / 砖墙${p2.pushDistance - 1}`, 0xff8800);
        gameAudio.playCoin();
        clearItemAt(world, this.blockCtx, c, r);
        if (multiplayerState.localPlayerId === 1) this.sendMultiplayerPower(1, p2);
        return;
      }
      gameAudio.playCoin();
    }
    clearItemAt(world, this.blockCtx, c, r);
  }
  private sendMultiplayerPower(playerId: 0 | 1, player: PlayerState | Player2State): void {
    this.sendMultiplayerMessage({
      type: 'player-power',
      playerId,
      levelIndex: this.currentLevelIndex,
      pushDistance: player.pushDistance,
      canBreakWalls: player.canBreakWalls,
    });
  }
  private applyRemotePlayerPower(msg: Extract<NetMessage, { type: 'player-power' }>): void {
    if (!this.isMultiplayer || msg.levelIndex !== this.currentLevelIndex) return;
    if (msg.playerId === multiplayerState.localPlayerId) return;
    const player = msg.playerId === 0 ? this.player : this.player2;
    if (!player) return;
    player.pushDistance = msg.pushDistance;
    player.canBreakWalls = msg.canBreakWalls;
  }
  private killEnemyScore(world: IWorld, p: PlayerState, ec: number, er: number) {
    _killEnemyScore(world, p, this.combo, ec, er, (eid) => this.trackEntity(eid));
  }

  // ------------------------------------------------------------------
  // Move player entity to new grid cell
  // ------------------------------------------------------------------
  private movePlayerTo(world: IWorld, p: PlayerState, tc: number, tr: number): void {
    const dc = tc - p.col;
    const dr = tr - p.row;
    this.grid[p.row][p.col] = this.safeZones.has(gridKey(p.col, p.row)) ? CELL_SAFE : CELL_EMPTY;
    this.grid[tr][tc] = CELL_PLAYER;

    p.col = tc;
    p.row = tr;
    p.cooldown = PLAYER_MOVE_COOLDOWN;
    p.moving = true;
    if (p === this.player) {
      this.e2eMoveCount += 1;
      this.publishE2EState();
    }
    gameAudio.playWalk();

    const target = gridToWorld(tc, tr);
    this.moveShadowTo(world, p.entity, target.x, target.y, 'actor', PLAYER_MOVE_COOLDOWN);
    this.movePlayerMarkerTo(world, p.entity, target.x, target.y, PLAYER_MOVE_COOLDOWN);
    const transform = world.getComponent<TransformComponent>(p.entity, TRANSFORM_COMPONENT);
    if (transform) {
      animateActorStepTransform(transform, target.x, target.y, dc, dr, PLAYER_MOVE_COOLDOWN);
      setTimeout(() => { p.moving = false; }, Math.round(PLAYER_MOVE_COOLDOWN * 1000));
    } else {
      p.moving = false;
    }
  }

  private isE2EEnabled(): boolean {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('e2e');
  }

  private getE2ELegalMoves(): E2EDirection[] {
    if (!this.player) return [];
    const dirs: E2EDirection[] = [
      { dc: 1, dr: 0, name: 'right' },
      { dc: 0, dr: 1, name: 'down' },
      { dc: -1, dr: 0, name: 'left' },
      { dc: 0, dr: -1, name: 'up' },
    ];

    return dirs.filter((dir) => {
      const col = this.player!.col + dir.dc;
      const row = this.player!.row + dir.dr;
      if (!inBounds(col, row)) return false;
      const cell = this.grid[row][col];
      return cell === CELL_EMPTY || cell === CELL_SAFE || cell === CELL_ITEM;
    });
  }

  private publishE2EState(): void {
    if (!this.isE2EEnabled()) return;
    window.__BRICKPUSH_E2E__ = {
      phase: this.phase,
      isMultiplayer: this.isMultiplayer,
      levelIndex: this.currentLevelIndex,
      moveCount: this.e2eMoveCount,
      player: this.player
        ? {
            col: this.player.col,
            row: this.player.row,
            moving: this.player.moving,
            cooldown: this.player.cooldown,
            hp: this.player.hp,
          }
        : null,
      legalMoves: this.getE2ELegalMoves(),
    };
  }

  private setPlayerTexture(world: IWorld, p: PlayerState | null, tex: string): void {
    if (!p) return;
    const sprite = world.getComponent<SpriteComponent>(p.entity, SPRITE_COMPONENT);
    if (sprite) sprite.textureId = tex;
  }

  private setPlayer2Texture(world: IWorld, p2: Player2State | null, tex: string): void {
    if (!p2) return;
    const sprite = world.getComponent<SpriteComponent>(p2.entity, SPRITE_COMPONENT);
    if (sprite) sprite.textureId = tex;
  }

  private setNpcTexture(world: IWorld, npc: NpcState | null, tex: string): void {
    if (!npc) return;
    const sprite = world.getComponent<SpriteComponent>(npc.entity, SPRITE_COMPONENT);
    if (sprite) sprite.textureId = tex;
  }

  // ------------------------------------------------------------------
  // Crush enemy (block push)
  // ------------------------------------------------------------------
  private crushEnemy(world: IWorld, p: PlayerState, enemy: EnemyState): void {
    this.killEnemyScore(world, p, enemy.col, enemy.row);
    enemy.dying = true;
    const idx = this.enemies.indexOf(enemy);
    if (idx >= 0) this.enemies.splice(idx, 1);
    if (this.grid[enemy.row][enemy.col] !== CELL_HEART_BLOCK &&
        this.grid[enemy.row][enemy.col] !== CELL_BLOCK &&
        this.grid[enemy.row][enemy.col] !== CELL_STAR_BLOCK &&
        this.grid[enemy.row][enemy.col] !== CELL_BOMB) {
      this.grid[enemy.row][enemy.col] = CELL_EMPTY;
    }

    const transform = world.getComponent<TransformComponent>(enemy.entity, TRANSFORM_COMPONENT);
    const sprite = world.getComponent<SpriteComponent>(enemy.entity, SPRITE_COMPONENT);

    if (transform) {
      // 立即停止所有正在运行的 tween，避免移动/挤压动画覆盖死亡动画
      globalTweens.killTweensOf(transform);
      if (sprite) globalTweens.killTweensOf(sprite);

      // 重置到当前格子中心，清除任何旋转/缩放残留
      const pos = gridToWorld(enemy.col, enemy.row);
      transform.x = pos.x;
      transform.y = pos.y;
      transform.scaleX = 1;
      transform.scaleY = 1;
      transform.rotation = 0;

      globalTweens.to(transform, { scaleX: 2.2, scaleY: 0.15 }, {
        duration: 0.08,
        easing: Easing.easeOutQuad,
        onComplete: () => {
          globalTweens.to(transform, { scaleX: 1.8, scaleY: 0.25 }, {
            duration: 0.08,
            easing: Easing.easeOutQuad,
            onComplete: () => {
              if (sprite) {
                globalTweens.to(sprite, { alpha: 0 }, {
	                  duration: 0.18,
	                  easing: Easing.easeInQuad,
	                  onComplete: () => {
	                    this.destroyShadowFor(world, enemy.entity);
	                    world.destroyEntity(enemy.entity);
	                    this.maybeDropItem(world, enemy.col, enemy.row, enemy.type);
	                  },
                });
	              } else {
	                this.destroyShadowFor(world, enemy.entity);
	                world.destroyEntity(enemy.entity);
	                this.maybeDropItem(world, enemy.col, enemy.row, enemy.type);
	              }
            },
          });
        },
      });
	    } else {
	      this.destroyShadowFor(world, enemy.entity);
	      world.destroyEntity(enemy.entity);
	      this.maybeDropItem(world, enemy.col, enemy.row, enemy.type);
	    }
	  }

	  private destroyEnemy(world: IWorld, enemy: EnemyState): void {
	    this.destroyShadowFor(world, enemy.entity);
	    world.destroyEntity(enemy.entity);
    const idx = this.enemies.indexOf(enemy);
    if (idx >= 0) this.enemies.splice(idx, 1);
    if (this.grid[enemy.row][enemy.col] !== CELL_HEART_BLOCK &&
      this.grid[enemy.row][enemy.col] !== CELL_BLOCK &&
      this.grid[enemy.row][enemy.col] !== CELL_STAR_BLOCK &&
      this.grid[enemy.row][enemy.col] !== CELL_BOMB) {
      this.grid[enemy.row][enemy.col] = CELL_EMPTY;
    }
  }

  private maybeDropItem(world: IWorld, c: number, r: number, enemyType?: number): void {
    if (this.grid[r][c] !== CELL_EMPTY) return;
    if (enemyType !== undefined && enemyType !== ENEMY_TYPE_FROG) return;
    this.spawnItemAt(world, c, r, ASSETS.ITEM_YELLOW);
  }

  private spawnScorePopup(world: IWorld, c: number, r: number, value: number, color: number, label?: string): void {
    _spawnScorePopup(world, c, r, value, color, label, (eid) => this.trackEntity(eid));
  }

  // ------------------------------------------------------------------
  // Spawn powerup text
  // ------------------------------------------------------------------
  private spawnPowerupText(world: IWorld, c: number, r: number, text: string, color: number): void {
    const pos = gridToWorld(c, r);
    const eid = EntityBuilder.create(world, W, H)
      .withTransform({ x: pos.x, y: pos.y })
      .withText({
        text: text,
        fontSize: 22,
        color,
        align: 'center',
        zIndex: Z_SCORE_POPUP
      })
      .build();
    this.trackEntity(eid);

    const transform = world.getComponent<TransformComponent>(eid, TRANSFORM_COMPONENT);
    if (transform) {
      globalTweens.to(transform, { y: pos.y - 60 }, {
        duration: 1.2,
        easing: Easing.easeOutQuad,
        onComplete: () => { world.destroyEntity(eid); },
      });
    }
    const textComp = world.getComponent<TextComponent>(eid, TEXT_COMPONENT);
    if (textComp) {
      globalTweens.to(textComp, { alpha: 0 }, { duration: 1.2, easing: Easing.linear });
    }
  }

  // ------------------------------------------------------------------
  // Enemy AI
  // ------------------------------------------------------------------
  private updateEnemies(world: IWorld, dt: number) { _updateEnemies(world, this.enemyAICtx, dt); }

  private spawnTongueAttack(world: IWorld, fromC: number, fromR: number, toC: number, toR: number): void {
    const from = gridToWorld(fromC, fromR);
    const to = gridToWorld(toC, toR);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.max(TILE_SIZE, Math.hypot(dx, dy) - TILE_SIZE * 0.24);
    const eid = EntityBuilder.create(world, W, H)
      .withTransform({
        x: from.x + dx / 2,
        y: from.y + dy / 2,
        rotation: Math.atan2(dy, dx),
        scaleX: 0.1,
        scaleY: 1,
      })
      .withSprite({
        textureId: ASSETS.CHAMELEON_TONGUE,
        width: length,
        height: TILE_SIZE * 0.34,
        zIndex: Z_ENEMY + 1,
      })
      .build();
    this.trackEntity(eid);

    const transform = world.getComponent<TransformComponent>(eid, TRANSFORM_COMPONENT);
    const sprite = world.getComponent<SpriteComponent>(eid, SPRITE_COMPONENT);
    if (sprite) sprite.alpha = 0.96;
    if (!transform) {
      world.destroyEntity(eid);
      return;
    }

    globalTweens.to(transform, { scaleX: 1 }, {
      duration: 0.06,
      easing: Easing.easeOutQuad,
      onComplete: () => {
        globalTweens.to(transform, { scaleX: 0.04 }, {
          duration: 0.12,
          easing: Easing.easeInQuad,
          delay: 0.06,
          onComplete: () => world.destroyEntity(eid),
        });
      },
    });
    if (sprite) {
      globalTweens.to(sprite, { alpha: 0 }, { duration: 0.12, delay: 0.12, easing: Easing.linear });
    }
  }

  // ------------------------------------------------------------------
  // NPC Squirrel AI
  // ------------------------------------------------------------------
  private updateNpc(world: IWorld, dt: number) { if (this.npc) _updateNpc(world, this.npcCtx, this.npc, dt); }
  private tryPushNpc(world: IWorld, p: PlayerState, npcC: number, npcR: number, dc: number, dr: number) { if (this.npc) _tryPushNpc(world, this.npcCtx, p, this.npc, npcC, npcR, dc, dr); }
  private damageNpc(world: IWorld, npc: NpcState) { _damageNpc(world, npc); }

  private findEnemyAt(c: number, r: number): EnemyState | null {
    for (const e of this.enemies) {
      if (e.col === c && e.row === r && e.active && !e.dying) return e;
    }
    return null;
  }

  private startEnemyRetreat(enemy: EnemyState, fromCol: number, fromRow: number): void {
    enemy.retreatSteps = 6;
    enemy.retreatFromCol = fromCol;
    enemy.retreatFromRow = fromRow;
    enemy.stunTimer = 0;
  }

  private updateWaves(world: IWorld, dt: number): void {
    if (this.currentWave >= this.totalWaves) return;
    if (this.nextWaveTimer <= 0) return;
    this.nextWaveTimer -= dt;
    if (this.nextWaveTimer <= 0) this.spawnNextWave(world);
  }

  // ------------------------------------------------------------------
  // Idle ear-wiggle animation (2-frame cycle)
  // ------------------------------------------------------------------
  private updateIdleAnimations(world: IWorld, dt: number): void {
    const INTERVAL = 0.35; // seconds per frame
    const p = this.player;
    const p2 = this.player2;
    const npc = this.npc;

    // ---- Player 1 & 2 idle ----
    let anyIdle = false;
    if (p && p.hp > 0 && !p.moving && p.cooldown <= 0) anyIdle = true;
    if (p2 && p2.hp > 0 && !p2.moving && p2.cooldown <= 0) anyIdle = true;

    if (!anyIdle) {
      this.idleTimer = 0;
      if (this.idleFrame !== 0) {
        this.idleFrame = 0;
        this.setPlayerTexture(world, p, ASSETS.PLAYER1);
        this.setPlayer2Texture(world, p2, ASSETS.PLAYER2);
      }
    } else {
      this.idleTimer += dt;
      if (this.idleTimer >= INTERVAL) {
        this.idleTimer -= INTERVAL;
        this.idleFrame = (this.idleFrame + 1) % 4; // 0→1→2→1→0 cycle
        const frameMap = [0, 1, 0, 2]; // normal, left, normal, right
        const frame = frameMap[this.idleFrame];

        const p1Tex = frame === 1 ? ASSETS.PLAYER1_IDLE_L : frame === 2 ? ASSETS.PLAYER1_IDLE_R : ASSETS.PLAYER1;
        const p2Tex = frame === 1 ? ASSETS.PLAYER2_IDLE_L : frame === 2 ? ASSETS.PLAYER2_IDLE_R : ASSETS.PLAYER2;

        this.setPlayerTexture(world, p, p1Tex);
        this.setPlayer2Texture(world, p2, p2Tex);
      }
    }

    // ---- NPC squirrel idle ----
    const npcIdle = npc && !npc.moving && npc.stunTimer <= 0;
    if (!npcIdle) {
      this.npcIdleTimer = 0;
      if (this.npcIdleFrame !== 0) {
        this.npcIdleFrame = 0;
        this.setNpcTexture(world, npc, ASSETS.PLAYER2);
      }
    } else {
      this.npcIdleTimer += dt;
      if (this.npcIdleTimer >= INTERVAL) {
        this.npcIdleTimer -= INTERVAL;
        this.npcIdleFrame = (this.npcIdleFrame + 1) % 4;
        const frameMap = [0, 1, 0, 2];
        const frame = frameMap[this.npcIdleFrame];
        const npcTex = frame === 1 ? ASSETS.PLAYER2_IDLE_L : frame === 2 ? ASSETS.PLAYER2_IDLE_R : ASSETS.PLAYER2;
        this.setNpcTexture(world, npc, npcTex);
      }
    }
  }

  // ------------------------------------------------------------------
  // HUD update
  // ------------------------------------------------------------------
  private updateHUD(world: IWorld): void {
    _updateHUD(world, this.hudEntities, this.player, this.enemies, this.timeLeft, this.currentLevelIndex, this.grid);
    if (this.isMultiplayer) {
      const hpText = (player: PlayerState | Player2State | null, id: 0 | 1) => {
        const hp = Math.max(0, player?.hp ?? this.multiplayerHp[id]);
        const hearts = '♥'.repeat(hp);
        const empty = '♡'.repeat(Math.max(0, PLAYER_MAX_HP - hp));
        return `P${id + 1}:${hearts}${empty}`;
      };
      setUIText(world, this.hudEntities.hpEntity, `${hpText(this.player, 0)}  ${hpText(this.player2, 1)}`);
    }
  }

  // ------------------------------------------------------------------
  // Heart victory
  // ------------------------------------------------------------------
  private checkHeartsConnected(): boolean { return checkHeartsConnected(this.grid); }

  // ------------------------------------------------------------------
  // Damage player
  // ------------------------------------------------------------------
  private damagePlayer(world: IWorld, playerId: 0 | 1 = 0): void {
    const target = playerId === 0 ? this.player : this.player2;
    if (!target || target.hp <= 0) return;
    if (this.isMultiplayer) {
      if (playerId !== multiplayerState.localPlayerId) return;
      _damagePlayer(world, target, () => {
        this.startMultiplayerPlayerDeathSequence(world, playerId, target);
        this.sendMultiplayerHealth(playerId, target.hp);
      });
      this.multiplayerHp[playerId] = Math.max(0, target.hp);
      this.sendMultiplayerHealth(playerId, target.hp);
      return;
    }
    if (!this.player) return;
    setRunHp(this.player.hp - 1);
    _damagePlayer(world, this.player, () => this.startPlayerDeathSequence(world));
    setRunHp(this.player.hp);
  }

  private getResolvedScore(): number {
    return Math.max(this.player?.score ?? 0, getRunScore());
  }

  private syncRunProgress(): void {
    setRunScore(this.getResolvedScore());
    if (this.player) {
      setRunHp(this.player.hp);
    }
  }

  private sendMultiplayerHealth(playerId: 0 | 1, hp: number): void {
    this.sendMultiplayerMessage({
      type: 'player-health',
      playerId,
      levelIndex: this.currentLevelIndex,
      hp: Math.max(0, Math.min(hp, PLAYER_MAX_HP)),
      dead: hp <= 0,
    });
  }

  private applyRemotePlayerHealth(world: IWorld, msg: Extract<NetMessage, { type: 'player-health' }>): void {
    if (!this.isMultiplayer || msg.levelIndex !== this.currentLevelIndex) return;
    if (msg.playerId === multiplayerState.localPlayerId) return;
    const player = msg.playerId === 0 ? this.player : this.player2;
    const hp = Math.max(0, Math.min(msg.hp, PLAYER_MAX_HP));
    this.multiplayerHp[msg.playerId] = hp;
    if (!player) return;
    player.hp = hp;
    if (msg.dead || hp <= 0) {
      this.startMultiplayerPlayerDeathSequence(world, msg.playerId, player);
    }
  }

  private startMultiplayerPlayerDeathSequence(world: IWorld, playerId: 0 | 1, player: PlayerState | Player2State): void {
    if (this.deadMultiplayerPlayers.has(playerId)) return;
    this.deadMultiplayerPlayers.add(playerId);
    this.multiplayerHp[playerId] = 0;
    player.hp = 0;
    player.moving = false;
    player.cooldown = 999;
    player.inputLockTimer = 999;
    this.grid[player.row][player.col] = this.safeZones.has(gridKey(player.col, player.row)) ? CELL_SAFE : CELL_EMPTY;

    const marker = this.playerMarkerMap.get(player.entity);
    if (marker !== undefined) {
      try { world.destroyEntity(marker); } catch { /* ignore */ }
      this.playerMarkerMap.delete(player.entity);
    }

    const transform = world.getComponent<TransformComponent>(player.entity, TRANSFORM_COMPONENT);
    const sprite = world.getComponent<SpriteComponent>(player.entity, SPRITE_COMPONENT);
    if (transform) {
      globalTweens.killTweensOf(transform);
      transform.rotation = 0;
      globalTweens.to(transform, { scaleX: 1.24, scaleY: 0.72 }, {
        duration: 0.12,
        easing: Easing.easeOutQuad,
        onComplete: () => {
          globalTweens.to(transform, { scaleX: 0.16, scaleY: 0.16, y: transform.y - TILE_SIZE * 0.18 }, {
            duration: 0.32,
            easing: Easing.easeInBack,
          });
        },
      });
    }
    if (sprite) {
      globalTweens.killTweensOf(sprite);
      sprite.alpha = 0.7;
      globalTweens.to(sprite, { alpha: 0 }, { duration: 0.42, delay: 0.12, easing: Easing.easeInQuad });
    }
    this.destroyShadowFor(world, player.entity);
    this.showMultiplayerDeathNotice(world, playerId);

    if (this.deadMultiplayerPlayers.size >= 2) {
      setTimeout(() => {
        if (!this.isActive) return;
        this.finishMultiplayerGameOver(world, 'hp', true);
      }, 620);
    }
  }

  private showMultiplayerDeathNotice(world: IWorld, playerId: 0 | 1): void {
    if (!this.isMultiplayer || this.phase !== 'playing') return;
    if (this.multiplayerDeathNoticeEntity) {
      try { world.destroyEntity(this.multiplayerDeathNoticeEntity); } catch { /* ignore */ }
      this.multiplayerDeathNoticeEntity = 0 as EntityId;
    }
    const isLocal = playerId === multiplayerState.localPlayerId;
    const eid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'top-center', y: 74, width: 360, height: 42 })
      .withText({
        text: isLocal ? '等待好友过关' : '好友倒下，继续过关',
        fontSize: 24,
        color: isLocal ? 0xffd6a0 : 0x77ddff,
        align: 'center',
        zIndex: Z_UI_POPUP,
      })
      .build();
    this.trackEntity(eid);
    this.multiplayerDeathNoticeEntity = eid;
  }

  // ------------------------------------------------------------------
  // Game over
  // ------------------------------------------------------------------
  private startPlayerDeathSequence(world: IWorld): void {
    if (!this.player || this.deathSequenceStarted) return;
    this.deathSequenceStarted = true;
    this.phase = 'complete';
    this.victoryType = 'none';
    this.completionHandled = true;

    const player = this.player;
    const transform = world.getComponent<TransformComponent>(player.entity, TRANSFORM_COMPONENT);
    const sprite = world.getComponent<SpriteComponent>(player.entity, SPRITE_COMPONENT);
    if (transform) {
      globalTweens.killTweensOf(transform);
      transform.rotation = 0;
      globalTweens.to(transform, { scaleX: 1.28, scaleY: 0.72 }, {
        duration: 0.12,
        easing: Easing.easeOutQuad,
        onComplete: () => {
          globalTweens.to(transform, { scaleX: 0.18, scaleY: 0.18, y: transform.y - TILE_SIZE * 0.2 }, {
            duration: 0.32,
            easing: Easing.easeInBack,
          });
        },
      });
    }
    if (sprite) {
      globalTweens.killTweensOf(sprite);
      sprite.alpha = 0.7;
      globalTweens.to(sprite, { alpha: 0 }, { duration: 0.42, delay: 0.12, easing: Easing.easeInQuad });
    }
    this.destroyShadowFor(world, player.entity);

    setTimeout(() => {
      if (!this.isActive) return;
      if (this.isMultiplayer) {
        this.finishMultiplayerGameOver(world, 'hp', true);
      } else {
        this.showGameOverText(world, 'GAME OVER');
        setTimeout(() => {
          if (!this.isActive) return;
          this.emitDefeatGameOver();
        }, 900);
      }
    }, 620);
  }

  private gameOver(world: IWorld, reason: 'hp' | 'time'): void {
    if (this.phase !== 'playing') return;
    if (reason === 'hp') {
      this.startPlayerDeathSequence(world);
      return;
    }
    if (this.isMultiplayer) {
      this.finishMultiplayerGameOver(world, reason, true);
      return;
    }
    this.phase = 'complete';
    this.victoryType = 'none';
    this.completeTimer = 0;
    this.showGameOverText(world, '时间到!');
    setTimeout(() => {
      if (!this.isActive) return;
      this.emitDefeatGameOver();
    }, 1500);
  }

  private emitDefeatGameOver(): void {
    const score = this.getResolvedScore();
    setRunScore(score);
    globalEventBus.emit('scene:gameover', {
      score,
      victoryType: 'defeat',
      levelName: LEVELS[this.currentLevelIndex]?.name ?? `ROUND-${this.currentLevelIndex + 1}`,
      canSubmitScore: score > 0,
    });
  }

  private showGameOverText(world: IWorld, text: string): void {
    const eid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -20, width: 500, height: 80 })
      .withText({ text, fontSize: 52, color: 0xff4444, align: 'center', zIndex: Z_UI_POPUP })
      .build();
    this.trackEntity(eid);
    this.victoryUIEntities.push(eid);
  }

  private finishMultiplayerGameOver(world: IWorld, reason: 'hp' | 'time' | 'disconnect', broadcast: boolean): void {
    if (this.multiplayerGameOverHandled) return;
    this.multiplayerGameOverHandled = true;
    this.phase = 'complete';
    this.victoryType = 'none';
    this.completeTimer = 0;
    this.completionHandled = true;

    if (broadcast) {
      this.sendMultiplayerMessage({
        type: 'multiplayer-game-over',
        reason,
        score: this.getResolvedScore(),
        levelIndex: this.currentLevelIndex,
      });
    }

    const title = reason === 'time' ? '时间到!' : reason === 'disconnect' ? '连接已断开' : '合作失败';
    const detail = reason === 'disconnect' ? '对方已离开房间' : '本局联机已结束';
    this.cleanupVictoryUI(world);
    this.showGameOverText(world, title);

    const detailEid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 42, width: 420, height: 42 })
      .withText({ text: detail, fontSize: 24, color: 0xffd6a0, align: 'center', zIndex: Z_UI_POPUP })
      .build();
    this.trackEntity(detailEid);
    this.victoryUIEntities.push(detailEid);

    const menuBtn = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 112, width: 220, height: 50 })
      .withButton({ label: '回到主菜单', onClick: 'scene:menu', borderRadius: 8 })
      .build();
    this.trackEntity(menuBtn);
    this.victoryUIEntities.push(menuBtn);
  }

  // ------------------------------------------------------------------
  // Check completion
  // ------------------------------------------------------------------
  private checkComplete(world: IWorld): void {
    if (this.phase !== 'playing' || this.clearScheduled) return;
    if (this.checkHeartsConnected()) {
      if (this.isMultiplayer) {
        this.scheduleLevelClear(world, true);
      } else {
        this.completeLevel(world, true);
      }
    }
  }

  private applyRemoteLevelClear(world: IWorld, msg: Extract<NetMessage, { type: 'level-clear' }>): void {
    if (!this.isMultiplayer || msg.levelIndex !== this.currentLevelIndex || this.phase !== 'playing') return;
    if (this.player) {
      this.player.score = Math.max(this.player.score, msg.score);
    }
    this.timeLeft = Math.max(this.timeLeft, msg.timeLeft);
    this.scheduleLevelClear(world, false);
  }

  private scheduleLevelClear(world: IWorld, broadcast: boolean): void {
    if (this.clearScheduled || this.phase !== 'playing') return;
    this.clearScheduled = true;
    setTimeout(() => {
      if (!this.isActive || this.phase !== 'playing') return;
      if (broadcast) {
        this.sendMultiplayerMessage({
          type: 'level-clear',
          playerId: multiplayerState.localPlayerId,
          levelIndex: this.currentLevelIndex,
          score: this.getResolvedScore(),
          timeLeft: this.timeLeft,
        });
      }
      this.completeLevel(world, broadcast);
    }, 320);
  }

  private completeLevel(world: IWorld, awardBonus: boolean): void {
    if (this.phase !== 'playing') return;
    this.phase = 'complete';
    this.victoryType = 'hearts';
    const levelClearBonus = this.currentLevelIndex + 1;
    const timeBonus = Math.floor(this.timeLeft * 100);
    if (awardBonus && this.player) this.player.score += SCORE_HEART_MERGE + levelClearBonus + timeBonus;
    if (awardBonus && timeBonus > 0) this.spawnScorePopup(world, 7, 5, timeBonus, 0x00ffcc, `⏱ TIME +${timeBonus}`);
    this.syncRunProgress();
    this.completeTimer = 2.5;
    if (this.isMultiplayer) {
      this.completionHandled = true;
    }
    gameAudio.playVictory();
    this.showVictoryText(world, '♥ 心心集合! ♥');
    this.playVictoryEffects(world);
  }

  private playVictoryEffects(world: IWorld): void {
    // 从屏幕中上部向上喷发礼花，多批次覆盖全屏
    const cx = W / 2;
    const cy = H * 0.75 - 200;
    this.spawnConfettiBurst(world, cx, cy);
    setTimeout(() => this.spawnConfettiBurst(world, cx - 80, cy + 20), 180);
    setTimeout(() => this.spawnConfettiBurst(world, cx + 80, cy + 20), 360);
    setTimeout(() => this.spawnConfettiBurst(world, cx, cy - 30), 540);
    // 主角和松鼠跳起来庆祝
    this.playVictoryJump(world);
  }

  private spawnConfettiBurst(world: IWorld, x: number, y: number): void {
    const colors = [0xff4444, 0xffd700, 0x44ff44, 0x4488ff, 0xff44ff, 0xff8844, 0x00ffff, 0xffffff];
    const count = 48;
    for (let i = 0; i < count; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const w = 4 + Math.random() * 6;
      const h = 3 + Math.random() * 4;
      // 严格向上扇形喷发：角度范围 -150° ~ -30°（Pixi Y轴向下，sin为负=向上）
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI * 2 / 3);
      const speed = 200 + Math.random() * 500;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed; // 负值 = 向上（Pixi坐标系）
      const eid = EntityBuilder.create(world, W, H)
        .withTransform({ x, y, rotation: Math.random() * Math.PI })
        .withSprite({ color, width: w, height: h, zIndex: 35 })
        .build();
      this.trackEntity(eid);
      this.victoryUIEntities.push(eid);
      const transform = world.getComponent<TransformComponent>(eid, TRANSFORM_COMPONENT);
      const sprite = world.getComponent<SpriteComponent>(eid, SPRITE_COMPONENT);
      if (transform) {
        const duration = 1.0 + Math.random() * 0.8;
        // 利用负vy让粒子向上飞，覆盖全屏（允许飞出屏幕上方）
        const tx = Math.max(-50, Math.min(W + 50, x + vx * 0.7));
        const ty = Math.max(-100, y + vy * 0.7);
        globalTweens.to(transform, { x: tx, y: ty, rotation: transform.rotation + (Math.random() - 0.5) * 8 }, {
          duration, easing: Easing.easeOutQuad,
        });
      }
      if (sprite) {
        globalTweens.to(sprite, { alpha: 0 }, {
          duration: 1.2, delay: 0.4, easing: Easing.easeInQuad,
          onComplete: () => { try { world.destroyEntity(eid); } catch { /* ignore */ } },
        });
      }
    }
  }

  private playVictoryJump(world: IWorld): void {
    const targets: Array<{ entity: EntityId; baseY: number }> = [];
    if (this.player) {
      const t = world.getComponent<TransformComponent>(this.player.entity, TRANSFORM_COMPONENT);
      if (t) targets.push({ entity: this.player.entity, baseY: t.y });
    }
    if (this.npc) {
      const t = world.getComponent<TransformComponent>(this.npc.entity, TRANSFORM_COMPONENT);
      if (t) targets.push({ entity: this.npc.entity, baseY: t.y });
    }
    for (const { entity, baseY } of targets) {
      const transform = world.getComponent<TransformComponent>(entity, TRANSFORM_COMPONENT);
      if (!transform) continue;
      for (let i = 0; i < 3; i++) {
        const d = i * 0.35;
        globalTweens.to(transform, { y: baseY - 30, scaleX: 0.92, scaleY: 1.08 }, {
          duration: 0.14, delay: d, easing: Easing.easeOutQuad,
        });
        globalTweens.to(transform, { y: baseY, scaleX: 1.0, scaleY: 1.0 }, {
          duration: 0.14, delay: d + 0.14, easing: Easing.easeInQuad,
        });
      }
    }
  }

  private showVictoryText(world: IWorld, text: string): void {
    this.victoryUIEntities = [];
    const levelName = LEVELS.length > 0 && this.currentLevelIndex < LEVELS.length
      ? LEVELS[this.currentLevelIndex].name
      : `ROUND-${this.currentLevelIndex + 1}`;
    const victoryText = `${levelName} CLEAR`;

    const textEid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -100, width: 600, height: 80 })
      .withText({
        text: victoryText,
        fontSize: 52,
        color: PALETTE.LEVEL_COMPLETE_GOLD,
        align: 'center',
        zIndex: Z_UI_POPUP,
      })
      .build();
    this.trackEntity(textEid);
    this.victoryUIEntities.push(textEid);

    const descEid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -30, width: 400, height: 40 })
      .withText({
        text,
        fontSize: 28,
        color: PALETTE.HEART_RED,
        align: 'center',
        zIndex: Z_UI_POPUP,
      })
      .build();
    this.trackEntity(descEid);
    this.victoryUIEntities.push(descEid);

    const hasNextLevel = LEVELS.length > 0 && this.currentLevelIndex < LEVELS.length - 1;

    if (this.isMultiplayer) {
      const readyBtn = UIEntityBuilder.create(world, W, H)
        .withUITransform({ anchor: 'center', y: 60, width: 220, height: 50 })
        .withButton({
          label: hasNextLevel ? '准备完成' : '完成合作',
          onClick: hasNextLevel ? 'custom:multiplayer-next-ready' : 'scene:menu',
          borderRadius: 8
        })
        .build();
      this.trackEntity(readyBtn);
      this.victoryUIEntities.push(readyBtn);

      const statusEid = UIEntityBuilder.create(world, W, H)
        .withUITransform({ anchor: 'center', y: 125, width: 360, height: 36 })
        .withText({
          text: hasNextLevel ? '等待双方确认' : '全部关卡完成',
          fontSize: 22,
          color: 0xffd6a0,
          align: 'center',
          zIndex: Z_UI_POPUP,
        })
        .build();
      this.trackEntity(statusEid);
      this.victoryUIEntities.push(statusEid);

      if (hasNextLevel) {
        this.multiplayerReadyDeadlineMs = Date.now() + 10000;
        const countdownEid = UIEntityBuilder.create(world, W, H)
          .withUITransform({ anchor: 'center', y: 154, width: 380, height: 30 })
          .withText({
            text: '10秒后自动继续',
            fontSize: 20,
            color: 0xffd6a0,
            align: 'center',
            zIndex: Z_UI_POPUP,
          })
          .build();
        this.trackEntity(countdownEid);
        this.multiplayerReadyCountdownEntity = countdownEid;
        this.victoryUIEntities.push(countdownEid);
      }

      const menuBtn = UIEntityBuilder.create(world, W, H)
        .withUITransform({ anchor: 'center', y: hasNextLevel ? 210 : 180, width: 220, height: 50 })
        .withButton({ label: '离开联机', onClick: 'scene:menu', borderRadius: 8 })
        .build();
      this.trackEntity(menuBtn);
      this.victoryUIEntities.push(menuBtn);
      return;
    }

    if (hasNextLevel) {
      const nextLevelBtn = UIEntityBuilder.create(world, W, H)
        .withUITransform({ anchor: 'center', y: 60, width: 200, height: 50 })
        .withButton({
          label: '下一关',
          onClick: 'custom:nextlevel',
          borderRadius: 8
        })
        .build();
      this.trackEntity(nextLevelBtn);
      this.victoryUIEntities.push(nextLevelBtn);
    }

    const menuBtn = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: hasNextLevel ? 130 : 60, width: 200, height: 50 })
      .withButton({ label: '回到主菜单', onClick: 'scene:menu', borderRadius: 8 })
      .build();
    this.trackEntity(menuBtn);
    this.victoryUIEntities.push(menuBtn);
  }

  private markMultiplayerNextReady(world: IWorld): void {
    if (!this.isMultiplayer || this.phase !== 'complete') return;
    const localPlayerId = multiplayerState.localPlayerId;
    const hasNextLevel = LEVELS.length > 0 && this.currentLevelIndex < LEVELS.length - 1;
    if (!hasNextLevel) {
      globalEventBus.emit('scene:menu');
      return;
    }
    if (!this.multiplayerNextReady.has(localPlayerId)) {
      this.multiplayerNextReady.add(localPlayerId);
      this.sendMultiplayerMessage({
        type: 'next-level-ready',
        playerId: localPlayerId,
        levelIndex: this.currentLevelIndex,
      });
    }
    this.refreshMultiplayerReadyStatus(world);
    this.maybeStartMultiplayerNextLevel(world);
  }

  private updateMultiplayerReadyCountdown(world: IWorld): void {
    if (!this.isMultiplayer || this.phase !== 'complete' || this.multiplayerReadyDeadlineMs <= 0) return;
    const hasNextLevel = LEVELS.length > 0 && this.currentLevelIndex < LEVELS.length - 1;
    if (!hasNextLevel) return;
    const secondsLeft = Math.max(0, Math.ceil((this.multiplayerReadyDeadlineMs - Date.now()) / 1000));
    setUIText(world, this.multiplayerReadyCountdownEntity, `${secondsLeft}秒后自动继续`);
    if (secondsLeft > 0) return;
    this.multiplayerReadyDeadlineMs = 0;
    this.markMultiplayerNextReady(world);
    this.maybeStartMultiplayerNextLevel(world, true);
  }

  private refreshMultiplayerReadyStatus(world: IWorld): void {
    if (!this.isMultiplayer || this.phase !== 'complete') return;
    if (this.multiplayerReadyStatusEntity) {
      try { world.destroyEntity(this.multiplayerReadyStatusEntity); } catch { /* ignore */ }
      this.victoryUIEntities = this.victoryUIEntities.filter((eid) => eid !== this.multiplayerReadyStatusEntity);
      this.multiplayerReadyStatusEntity = 0 as EntityId;
    }
    const p1 = this.multiplayerNextReady.has(0) ? 'P1已准备' : 'P1等待中';
    const p2 = this.multiplayerNextReady.has(1) ? 'P2已准备' : 'P2等待中';
    const eid = UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 232, width: 420, height: 34 })
      .withText({
        text: `${p1} / ${p2}`,
        fontSize: 20,
        color: 0x77ddff,
        align: 'center',
        zIndex: Z_UI_POPUP,
      })
      .build();
    this.trackEntity(eid);
    this.multiplayerReadyStatusEntity = eid;
    this.victoryUIEntities.push(eid);
  }

  private maybeStartMultiplayerNextLevel(world: IWorld, force = false): void {
    if (!this.isMultiplayer) return;
    if (!force && (!this.multiplayerNextReady.has(0) || !this.multiplayerNextReady.has(1))) return;
    if (this.currentLevelIndex >= LEVELS.length - 1) return;

    if (multiplayerState.localPlayerId === 0) {
      const levelIndex = this.currentLevelIndex + 1;
      const seed = Math.floor(Math.random() * 1000000);
      this.sendMultiplayerMessage({ type: 'load-level', levelIndex, seed });
      this.loadMultiplayerLevel(world, levelIndex, seed);
    }
  }

  private loadMultiplayerLevel(world: IWorld, levelIndex: number, seed: number): void {
    if (!this.isMultiplayer) return;
    if (levelIndex < 0 || levelIndex >= LEVELS.length) return;
    this.prepareMultiplayerHpForNextLevel();
    setRunScore(this.getResolvedScore());
    setRunHp(this.player?.hp ?? getRunHp());
    this.cleanupAllEntities(world);
    this.currentLevelIndex = levelIndex;
    setCurrentLevelIndex(this.currentLevelIndex);
    multiplayerState.gameSeed = seed;
    this.rng = seedRandom(seed).random;
    this.resetState();
    this.timeLeft = getLevelTimeLimit(this.currentLevelIndex, Math.max(LEVELS.length, 1));
    this.registerNextLevelHandler(world);
    this.parseLevel();
    this.createFloor(world);
    this.createEntitiesFromGrid(world);
    this.createHUD(world);
    this.createReadyOverlay(world);
    this.setupTouchJoystick();
    this.setupKeyboardInputQueue();
    this.setupMultiplayerPeerHandlers(world);
    this.phase = 'ready';
    this.readyTimer = READY_DURATION;
    this.completionHandled = false;
    this.multiplayerNextReady.clear();
    this.multiplayerGameOverHandled = false;
    this.pendingLocalDirections = [];
    this.remoteCommandQueue = [];
    this.lastRemoteCommandSeq.clear();
    this.clearScheduled = false;
    this.multiplayerReadyCountdownEntity = 0 as EntityId;
    this.multiplayerReadyDeadlineMs = 0;
    this.multiplayerDeathNoticeEntity = 0 as EntityId;
  }

  private prepareMultiplayerHpForNextLevel(): void {
    this.multiplayerHp[0] = this.deadMultiplayerPlayers.has(0)
      ? 1
      : Math.max(1, Math.min(this.player?.hp ?? this.multiplayerHp[0], PLAYER_MAX_HP));
    this.multiplayerHp[1] = this.deadMultiplayerPlayers.has(1)
      ? 1
      : Math.max(1, Math.min(this.player2?.hp ?? this.multiplayerHp[1], PLAYER_MAX_HP));
    this.deadMultiplayerPlayers.clear();
  }

  private goToNextLevel(world: IWorld): void {
    if (this.isMultiplayer) return;
    if (this.currentLevelIndex >= LEVELS.length - 1) return;
    if (this.phase !== 'complete') return;
    this.phase = 'ready';
    this.cleanupAllEntities(world);
    setRunScore(this.getResolvedScore());
    setRunHp(this.player?.hp ?? getRunHp());
    this.currentLevelIndex++;
    setCurrentLevelIndex(this.currentLevelIndex);
    this.resetState();
    this.timeLeft = getLevelTimeLimit(this.currentLevelIndex, Math.max(LEVELS.length, 1));
    this.registerNextLevelHandler(world);
    this.parseLevel();
    this.createFloor(world);
    this.createEntitiesFromGrid(world);
    this.createHUD(world);
    if (!this.maybeShowSquirrelCutscene(world)) {
      this.createReadyOverlay(world);
    }
    this.setupTouchJoystick();
    this.phase = 'ready';
    this.readyTimer = READY_DURATION;
    this.completionHandled = false;
  }

  // ------------------------------------------------------------------
  // Cleanup all entities
  // ------------------------------------------------------------------
  private cleanupAllEntities(world: IWorld): void {
    this.cleanupVictoryUI(world);
    this.destroyTrackedEntities(world);
    if (this.nonGridEntities) {
      this.nonGridEntities.clear();
    }
    this.entityMap.clear();
    this.bombs = [];
    this.enemies = [];
    this.shadowMap.clear();
    this.playerMarkerMap.clear();
    this.player = null;
    this.player2 = null;
    this.safeZones.clear();
    this.grid = [];
    this.hudEntities = { hpEntity: 0 as EntityId, scoreDisplayEntity: 0 as EntityId, timeDisplayEntity: 0 as EntityId, levelDisplayEntity: 0 as EntityId, heartStatusEntity: 0 as EntityId, enemyCountEntity: 0 as EntityId, scoreEntity: 0 as EntityId, collectEntity: 0 as EntityId, readyEntity: 0 as EntityId };
    this.readyEntity = 0;
    this.multiplayerReadyStatusEntity = 0 as EntityId;
    this.multiplayerReadyCountdownEntity = 0 as EntityId;
    this.multiplayerReadyDeadlineMs = 0;
    this.multiplayerDeathNoticeEntity = 0 as EntityId;
  }

  // ------------------------------------------------------------------
  // Track entity for cleanup
  // ------------------------------------------------------------------
  protected override trackEntity(eid: EntityId): void {
    super.trackEntity(eid);
    if (!this.nonGridEntities) {
      this.nonGridEntities = new Set<EntityId>();
    }
    this.nonGridEntities.add(eid);
  }

  private addShadowFor(world: IWorld, owner: EntityId, x: number, y: number, kind: ShadowKind, zIndex: number): void {
    const shadow = createGroundShadow(world, x, y, (eid) => this.trackEntity(eid), { kind, zIndex });
    this.shadowMap.set(owner, shadow);
  }

  private moveShadowTo(world: IWorld, owner: EntityId, x: number, y: number, kind: ShadowKind, duration = 0): void {
    moveGroundShadowTo(world, this.shadowMap.get(owner), x, y, kind, duration);
  }

  private destroyShadowFor(world: IWorld, owner: EntityId): void {
    const shadow = this.shadowMap.get(owner);
    if (shadow !== undefined) {
      world.destroyEntity(shadow);
      this.shadowMap.delete(owner);
    }
  }

  // ------------------------------------------------------------------
  // Cleanup victory UI
  // ------------------------------------------------------------------
  private cleanupVictoryUI(world: IWorld): void {
    for (const eid of this.victoryUIEntities) {
      world.destroyEntity(eid);
    }
    this.victoryUIEntities = [];
    this.multiplayerReadyStatusEntity = 0 as EntityId;
    this.multiplayerReadyCountdownEntity = 0 as EntityId;
    this.multiplayerReadyDeadlineMs = 0;
  }

  // ------------------------------------------------------------------
  // onExit
  // ------------------------------------------------------------------
  onExit(world: IWorld): void {
    for (const h of this.touchHandlers) {
      globalEventBus.off(h.evt, h.fn);
    }
    this.touchHandlers = [];
    this.grid = [];
    this.entityMap.clear();
    this.safeZones.clear();
    this.player = null;
    this.player2 = null;
    this.enemies = [];
    this.bombs = [];
    this.shadowMap.clear();
    this.playerMarkerMap.clear();
    this.cutsceneActive = false;
    this.cutsceneEntities = [];
    this.phase = 'ready';
    this.readyTimer = READY_DURATION;
    this.completeTimer = 0;
    this.victoryType = 'none';
    this.timeLeft = TIME_LIMIT_SECONDS;
    this.completionHandled = false;
    this.multiplayerNextReady.clear();
    this.multiplayerGameOverHandled = false;
    this.multiplayerReadyStatusEntity = 0 as EntityId;
    this.multiplayerReadyCountdownEntity = 0 as EntityId;
    this.multiplayerReadyDeadlineMs = 0;
    this.multiplayerDeathNoticeEntity = 0 as EntityId;
    this.clearScheduled = false;
    this.touchDir = null;
    this.touchActive = false;
    if (this.touchCleanup) {
      this.touchCleanup();
      this.touchCleanup = null;
    }
    if (this.keyboardCleanup) {
      this.keyboardCleanup();
    }
    if (this.isMultiplayer) {
      multiplayerState.cleanup();
    }
    super.onExit(world);
  }

  // ------------------------------------------------------------------
  // Modify push distance (for power-ups)
  // ------------------------------------------------------------------
  public modifyPushDistance(distance: number): void {
    if (this.player) {
      const newDistance = Math.max(1, Math.min(distance, PLAYER_MAX_PUSH_DISTANCE));
      this.player.pushDistance = newDistance;
      console.log(`Push distance changed to ${newDistance}`);
    }
  }

  // ------------------------------------------------------------------
  // Reset push distance to default
  // ------------------------------------------------------------------
  public resetPushDistance(): void {
    if (this.player) {
      this.player.pushDistance = PLAYER_PUSH_DISTANCE;
    }
  }

  // ------------------------------------------------------------------
  // AABB collision detection (action-game style)
  // Runs every frame after all positions have been updated by tweens
  // ------------------------------------------------------------------
  private checkAABBCollisions(world: IWorld): void {
    const COLLIDER_SIZE = TILE_SIZE * 0.83; // slightly smaller than TILE_SIZE

    // Helper: get bounds or null
    const getBounds = (entity: EntityId) => getEntityBounds(world, entity, COLLIDER_SIZE, COLLIDER_SIZE);

    // 1. Player vs active enemies
    if (this.player && this.player.hp > 0 && this.player.damageCooldown <= 0) {
      const pBounds = getBounds(this.player.entity);
      if (pBounds) {
        for (const enemy of this.enemies) {
          if (!enemy.active || enemy.dying) continue;
          const eBounds = getBounds(enemy.entity);
          if (eBounds && checkAABBCollision(pBounds, eBounds)) {
            this.damagePlayer(world, 0);
            this.startEnemyRetreat(enemy, this.player.col, this.player.row);
            break; // only one damage per frame
          }
        }
      }
    }

    // 2. Player2 vs active enemies
    if (this.player2 && this.player2.hp > 0 && this.player2.damageCooldown <= 0) {
      const p2Bounds = getBounds(this.player2.entity);
      if (p2Bounds) {
        for (const enemy of this.enemies) {
          if (!enemy.active || enemy.dying) continue;
          const eBounds = getBounds(enemy.entity);
          if (eBounds && checkAABBCollision(p2Bounds, eBounds)) {
            this.damagePlayer(world, 1);
            this.startEnemyRetreat(enemy, this.player2.col, this.player2.row);
            break;
          }
        }
      }
    }

    // 3. Moving blocks vs enemies (box crushes enemy)
    // 反向遍历，避免 crushEnemy splice 数组导致跳过元素
    for (const blockEntity of this.movingBlocks) {
      const bBounds = getBounds(blockEntity);
      if (!bBounds) continue;
      for (let i = this.enemies.length - 1; i >= 0; i--) {
        const enemy = this.enemies[i];
        if (!enemy.active || enemy.dying) continue;
        const eBounds = getBounds(enemy.entity);
        if (eBounds && checkAABBCollision(bBounds, eBounds)) {
          if (this.player) {
            this.crushEnemy(world, this.player, enemy);
          } else {
            // 无玩家计分：快速自毁（与 crushEnemy 保持相同时长）
            enemy.dying = true;
            this.enemies.splice(i, 1);
            const t2 = world.getComponent<TransformComponent>(enemy.entity, TRANSFORM_COMPONENT);
            const s2 = world.getComponent<SpriteComponent>(enemy.entity, SPRITE_COMPONENT);
            if (t2) {
              globalTweens.killTweensOf(t2);
              if (s2) globalTweens.killTweensOf(s2);
              const pos2 = gridToWorld(enemy.col, enemy.row);
              t2.x = pos2.x; t2.y = pos2.y;
              t2.scaleX = 1; t2.scaleY = 1; t2.rotation = 0;
              globalTweens.to(t2, { scaleX: 2.2, scaleY: 0.15 }, {
                duration: 0.08, easing: Easing.easeOutQuad,
                onComplete: () => {
                  globalTweens.to(t2, { scaleX: 1.8, scaleY: 0.25 }, {
                    duration: 0.08, easing: Easing.easeOutQuad,
                    onComplete: () => {
                      if (s2) {
                        globalTweens.to(s2, { alpha: 0 }, {
                          duration: 0.18, easing: Easing.easeInQuad,
                          onComplete: () => world.destroyEntity(enemy.entity),
                        });
                      } else {
                        world.destroyEntity(enemy.entity);
                      }
                    },
                  });
                },
              });
            } else {
              world.destroyEntity(enemy.entity);
            }
          }
          break; // 一个 block 每帧只压死一个敌人
        }
      }
    }

    // 4. Enemies vs NPC
    if (this.npc && !this.npc.isInvincible && this.npc.damageCooldown <= 0) {
      const npcBounds = getBounds(this.npc.entity);
      if (npcBounds) {
        for (const enemy of this.enemies) {
          if (!enemy.active || enemy.dying) continue;
          const eBounds = getBounds(enemy.entity);
          if (eBounds && checkAABBCollision(npcBounds, eBounds)) {
            this.damageNpc(world, this.npc);
            enemy.stunTimer = 1.2;
            break;
          }
        }
      }
    }
  }
}
